

import { FONT_MONO } from 'styles/tokens/typography';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Box,
  Card,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Alert,
  Snackbar,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  ButtonGroup,
  CircularProgress,
  FormHelperText,
  Checkbox,
  Button,
} from '@mui/material';
import {
  Close,
  NavigateNext,
  NavigateBefore,
  Add,
  Delete,
  Edit,
  CheckCircle,
  PlayArrow,
  Schedule,
  AccountTree,
  Settings,
  Info,
  AccessTime,
  Visibility as VisibilityIcon,
  ContentCopy,
  Email,
  Webhook,
  Refresh,
  Update
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { useAuth } from '../../auth/Auth';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useCurrency } from '../../context/CurrencyContext';
import { useTravelUi } from 'hooks/useTravelUi';


// Import modal components for creating records
import AccountFormModal from 'components/NewAccountForm';
import ContactFormModal from 'components/NewContactModal';
import LeadFormModal from 'components/NewLeadModal';
import AddCaseModal from 'components/CustomModals/AddCaseModal';
import OrderModal from 'components/CustomModals/AddOrderModal';
import QuotationModal from 'components/CustomModals/AddQuotationModal';

/** Legacy commerce objects replaced by Travel when the travel toggle is on. */
const LEGACY_CRM_AUTOMATION_OBJECTS = new Set(['order', 'quotation', 'product']);
/** Travel objects hidden from automations when travel is off. */
const TRAVEL_AUTOMATION_OBJECTS = new Set([
  'travel_orders',
  'trip',
  'payment_template',
  'cms_publisher',
  'trip_option',
]);

const filterAutomationObjectIds = (ids, showTravel) =>
  (ids || []).filter((id) => {
    if (showTravel && LEGACY_CRM_AUTOMATION_OBJECTS.has(id)) return false;
    if (!showTravel && TRAVEL_AUTOMATION_OBJECTS.has(id)) return false;
    return true;
  });

/** Create Record targets hidden from *new* picks when travel UI is on. */
const LEGACY_CREATE_RECORD_TYPES = ['order', 'quotation', 'product'];


// Add this constant for audit fields to exclude
const AUDIT_FIELDS = [
  'created_at',
  'updated_at',
  'created_by',
  'created_by_name',
  'updated_by',
  'updated_by_name',
  'deleted_at',
  'deleted_by',
  'last_modified_date',
  'last_modified_by',
  'system_modstamp'
];

// Re-insert the steps array for the stepper
const steps = [
  { label: 'Trigger', icon: <PlayArrow /> },
  { label: 'Conditions', icon: <Settings /> },
  { label: 'Actions', icon: <AccountTree /> },
  { label: 'Review', icon: <CheckCircle /> }
];

// Add this custom filter function near the top of the file, after imports:
const referenceFilterOptions = (options, { inputValue }) => {
  const search = inputValue.trim().toLowerCase();
  return options.filter(option =>
    (option.label || '').toLowerCase().includes(search)
  );
};

// Debounce function
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

// Define triggers as a local constant if not available from backend
const TRIGGERS = [
          { id: "on create", name: "On Create" },
        { id: "on update", name: "On Update" },
        { id: "on delete", name: "On Delete" }
];

// Function to get available triggers based on object type
const getAvailableTriggers = (objectType) => {
  // Don't show "on delete" for invoices and orders since they don't support deletion
  if (objectType === 'invoice' || objectType === 'order') {
    return TRIGGERS.filter(trigger => trigger.id !== "on delete");
  }
  return TRIGGERS;
};

// Operators are now fetched from backend API - no hardcoded constants

const ACTION_TYPES = [
  { id: "Update Field", name: "Update Field", description: "Update a field value on the record", icon: "edit" },
  { id: "Create Task", name: "Create Task", description: "Create a new task", icon: "task" },
  { id: "Send Email", name: "Send Email", description: "Send an email using a template", icon: "email" },
  { id: "Create Record", name: "Create Record", description: "Create a new record", icon: "add" },
  { id: "Update Related Record", name: "Update Related Record", description: "Update a related record", icon: "update" },
  { id: "Webhook", name: "Webhook", description: "Send data to an external webhook URL", icon: "link" }
];

// Add this helper at the top (after imports):
function getContactLabel(item) {
  if (item.first_name && item.last_name) return `${item.first_name} ${item.last_name}`;
  if (item.last_name) return item.last_name;
  if (item.first_name) return item.first_name;
  return 'Unknown';
}

// Helper function to determine if a field should be treated as datetime
function isDateTimeField(field) {
  if (!field) return false;
  
  // Check if field type is explicitly datetime
  if (field.type === 'datetime') return true;
  
  // Check for specific field names that should be treated as datetime
  const datetimeFieldNames = [
    'order_date', 'created_date', 'updated_date', 'due_date', 'start_date', 'end_date',
    'delivery_date', 'shipping_date', 'invoice_date', 'payment_date', 'close_date',
    'last_activity_date', 'last_contact_date', 'next_follow_up_date'
  ];
  
  return datetimeFieldNames.includes(field.id) || 
         datetimeFieldNames.some(name => field.id.includes(name));
}

// Helper function to get operator name from backend API
async function getOperatorName(operatorId, fieldType, axiosInstance) {
  try {
    const response = await axiosInstance.get(`/operators/${fieldType}`);
    const operator = response.data.find(o => o.id === operatorId);
    return operator?.name || operatorId;
  } catch (error) {
    console.error('Error fetching operator name:', error);
    return operatorId;
  }
}



// Enhanced Autocomplete component for reference fields with "Create New" option
const ReferenceAutocomplete = React.memo(({ field, value, onChange, actionIndex = null, onOpenCreateModal, referenceMap, referenceDataCache, referenceDataLoading, fetchReferenceData, prettify, isCreatingRecord, selectedAccountForDeals }) => {
  //console.log('🔄 ReferenceAutocomplete RENDER:', field.id, 'value:', value, 'actionIndex:', actionIndex);
  
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [error, setError] = useState(null);
  const [inputValue, setInputValue] = useState('');

  // Get endpoint and labelField from referenceMap
  const { endpoint, labelField } = referenceMap[field.id] || {};
  


  // Get cached data and loading state
  const cachedOptions = referenceDataCache[endpoint] || [];
  const isLoading = referenceDataLoading[endpoint] || false;



  // Determine if this field supports creating new records
  const recordType = field.id.replace('_id', '');
  const canCreateNew = ['account', 'contact', 'lead', 'case', 'order', 'quotation'].includes(recordType);

  // Load data on mount if not cached
  useEffect(() => {
    const loadData = async () => {
      try {
        const options = await fetchReferenceData(endpoint, labelField);
        if (options) {
          setFilteredOptions(options);
        }
      } catch (error) {
        console.error('ReferenceAutocomplete fetch error for field:', field.id, error);
        setError(`Failed to fetch options: ${error.message}`);
      }
    };

    // For deal fields with selected account, check the account-specific cache
    let optionsToUse = cachedOptions;
    if (field.id === 'deal_id' && selectedAccountForDeals) {
      const accountDealsEndpoint = `/accounts/deals/${selectedAccountForDeals}`;
      optionsToUse = referenceDataCache[accountDealsEndpoint] || [];
    }

    if (!optionsToUse.length && !isLoading) {
      loadData();
    } else if (optionsToUse.length) {
      setFilteredOptions(optionsToUse);
    }
  }, [endpoint, labelField, cachedOptions.length, isLoading, fetchReferenceData, field.id, selectedAccountForDeals]);

  // Handle value change
  const handleChange = useCallback((_, newValue) => {
    if (newValue && newValue.isCreateNew) {
      // Open the create modal for this record type
      if (onOpenCreateModal) {
        onOpenCreateModal(recordType, actionIndex, field.id);
      }
      return;
    }
    onChange(newValue ? newValue.id : '');
  }, [onChange, recordType, actionIndex, field.id, onOpenCreateModal]);

  // Handle input change for filtering
  const handleInputChange = useCallback((_, newInputValue) => {
    setInputValue(newInputValue);
    
    // For deal fields with selected account, use the account-specific cache
    let optionsToFilter = cachedOptions;
    if (field.id === 'deal_id' && selectedAccountForDeals) {
      const accountDealsEndpoint = `/accounts/deals/${selectedAccountForDeals}`;
      optionsToFilter = referenceDataCache[accountDealsEndpoint] || [];
    }
    
    if (newInputValue) {
      // Filter existing cached options - no API call
      const filtered = optionsToFilter.filter(option =>
        option.label.toLowerCase().includes(newInputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      // Show all cached options if input is empty
      setFilteredOptions(optionsToFilter);
    }
  }, [cachedOptions, field.id, selectedAccountForDeals]);

    // Set inputValue when value changes (to show selected option label)
  useEffect(() => {
    if (value) {
      // For deal fields with selected account, we need to check the correct cache
      let optionsToSearch = cachedOptions;
      
      if (field.id === 'deal_id' && selectedAccountForDeals) {
        // For deal fields with selected account, use the account-specific cache
        const accountDealsEndpoint = `/accounts/deals/${selectedAccountForDeals}`;
        optionsToSearch = referenceDataCache[accountDealsEndpoint] || [];
      }
      
      if (optionsToSearch.length > 0) {
        // Try both string and number comparison for the ID
        const selectedOption = optionsToSearch.find(opt => opt.id === value || opt.id === String(value) || opt.id === Number(value));
        if (selectedOption) {
          setInputValue(selectedOption.label);
        }
      }
    } else {
      setInputValue('');
    }
  }, [value, cachedOptions, field.id, selectedAccountForDeals]);

  // Always show "Create New" option
  const optionsWithCreateNew = useMemo(() => {
    if (!canCreateNew) {
      return filteredOptions;
    }
    
    return [
      {
        id: `create_new_${recordType}`,
        label: `Create new ${recordType}`,
        isCreateNew: true
      },
      ...filteredOptions
    ];
  }, [filteredOptions, canCreateNew, recordType]);

  return (
    <>
      <Autocomplete
        fullWidth
        size="small"
        options={optionsWithCreateNew}
        getOptionLabel={option => option.label || ''}
        isOptionEqualToValue={(option, value) => {
          if (!option || !value) return false;
          return option.id === value.id || option.id === value;
        }}
        value={(() => {
          // For deal fields with selected account, we need to check the correct cache
          let optionsToSearch = cachedOptions;
          
          if (field.id === 'deal_id' && selectedAccountForDeals) {
            // For deal fields with selected account, use the account-specific cache
            const accountDealsEndpoint = `/accounts/deals/${selectedAccountForDeals}`;
            optionsToSearch = referenceDataCache[accountDealsEndpoint] || [];
          }
          
          // Try both string and number comparison for the ID
          const foundOption = optionsToSearch.find(opt => opt.id === value || opt.id === String(value) || opt.id === Number(value));
          return foundOption || null;
        })()}
        onChange={handleChange}
        onInputChange={handleInputChange}
        inputValue={inputValue}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            {option.isCreateNew ? (
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Add sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body2" color="primary.main" fontWeight="medium">
                  {option.label}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2">{option.label}</Typography>
            )}
          </Box>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label={field.label || prettify(field.id)}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading || isCreatingRecord ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        loading={isLoading || isCreatingRecord}
        noOptionsText={isLoading || isCreatingRecord ? "Loading..." : "No options"}
        filterOptions={(x) => x} // Disable built-in filtering since we handle it manually
        disabled={isCreatingRecord}
      />
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </>
  );
});

// Add OperatorSelect component
function OperatorSelect({ condition, group, updateConditionInGroup, getAvailableFields }) {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { axiosInstance } = useAuth();

  const selectedField = getAvailableFields().find(f => f.id === condition.field);

  useEffect(() => {
    const fetchOperators = async () => {
      if (selectedField?.type) {
        setLoading(true);
        setError(null);
        try {
          const response = await axiosInstance.get(`/operators/${selectedField.type}`);
          //console.log('Fetched operators:', response.data);
          setOperators(response.data || []);
        } catch (error) {
          console.error('Error fetching operators:', error);
          setError('Failed to load operators');
          setOperators([]);
        } finally {
          setLoading(false);
        }
      } else {
        setOperators([]);
      }
    };
    fetchOperators();
  }, [selectedField?.type, axiosInstance]);

  return (
    <FormControl fullWidth size="small">
      <InputLabel>Operator</InputLabel>
      <Select
        value={condition.operator || ''}
        onChange={(e) => updateConditionInGroup(group.id, condition.id, 'operator', e.target.value)}
        label="Operator"
        disabled={!condition.field}
        sx={{
          height: '40px',
          '& .MuiSelect-select': {
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            padding: '0 14px'
          }
        }}
      >
        {loading && <MenuItem disabled>Loading operators...</MenuItem>}
        {error && <MenuItem disabled>Error loading operators</MenuItem>}
        {!loading && !error && operators.length === 0 && <MenuItem disabled>No operators available</MenuItem>}
        {operators.map(op => (
          <MenuItem key={op.id} value={op.id}>
            {op.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// Add OperatorOptions component
function OperatorOptions({ field }) {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { axiosInstance } = useAuth();

  useEffect(() => {
    const fetchOperators = async () => {
      if (field?.type) {
       
        setLoading(true);
        setError(null);
        try {
          const response = await axiosInstance.get(`/operators/${field.type}`);
          //console.log('Fetched operators:', response.data);
          
          setOperators(response.data || []);
        } catch (error) {
          console.error('Error fetching operators:', error);
          setError('Failed to load operators');
          setOperators([]);
        } finally {
          setLoading(false);
        }
      } else {
        setOperators([]);
      }
    };
    fetchOperators();
  }, [field?.type, axiosInstance]);

  if (loading) {
    return <MenuItem disabled>Loading operators...</MenuItem>;
  }

  if (error) {
    return <MenuItem disabled>Error loading operators</MenuItem>;
  }

  if (operators.length === 0) {
    return <MenuItem disabled>No operators available</MenuItem>;
  }

  return operators.map(op => (
    <MenuItem key={op.id} value={op.id}>
      {op.name}
    </MenuItem>
  ));
}

// Relative date input for date fields (supports exact vs relative)
function RelativeDateInput({ label, value, onChange }) {
  const isObject = value && typeof value === 'object';
  const isRelative = isObject && value.mode === 'relative';

  const handleToggle = (useRelative) => {
    if (useRelative) {
      onChange({ mode: 'relative', amount: 0, unit: 'days' });
    } else {
      onChange('');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <FormControlLabel
        control={
          <Switch
            checked={isRelative}
            onChange={(e) => handleToggle(e.target.checked)}
          />
        }
        label="Use relative date"
      />
      <Typography variant="caption" color="text.secondary">
        Relative date sets the value based on when the automation runs. For example,
        10 days means the date will be 10 days after the trigger time.
      </Typography>
      {isRelative ? (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            label="Value"
            type="number"
            value={(isObject && typeof value.amount === 'number') ? value.amount : 0}
            onChange={(e) => onChange({ mode: 'relative', amount: parseInt(e.target.value) || 0, unit: (isObject && value.unit) || 'days' })}
            sx={{
              width: '40%',
              '& .MuiInputBase-root': { height: '40px' }
            }}
            InputProps={{ inputProps: { min: 0 } }}
          />
          <FormControl size="small" sx={{ width: '60%' }}>
            <InputLabel>Unit</InputLabel>
            <Select
              label="Unit"
              value={(isObject && value.unit) || 'days'}
              onChange={(e) => onChange({ mode: 'relative', amount: (isObject && value.amount) || 0, unit: e.target.value })}
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 14px',
                  minHeight: '40px'
                },
                '& .MuiOutlinedInput-root': { minHeight: '40px' }
              }}
            >
              <MenuItem value="days">Days</MenuItem>
              <MenuItem value="weeks">Weeks</MenuItem>
              <MenuItem value="months">Months</MenuItem>
              <MenuItem value="years">Years</MenuItem>
            </Select>
          </FormControl>
        </Box>
      ) : (
        <TextField
          fullWidth
          size="small"
          type="date"
          label={label}
          value={isObject ? '' : (value || '')}
          onChange={(e) => onChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ '& .MuiInputBase-root': { height: '40px' } }}
        />
      )}
    </Box>
  );
}

// Add ValueField component
function ValueField({ condition, field, group, updateConditionInGroup, getPicklistOptions, referenceMap, referenceDataCache, referenceDataLoading, fetchReferenceData, prettify, isCreatingRecord }) {
  const [operator, setOperator] = useState(null);
  const { axiosInstance } = useAuth();
  const { availableCurrencies } = useCurrency();

  

  // Add debugging
  // // console.log('ValueField props:', { condition, field, group });

// Fetch operator details from backend API
useEffect(() => {
  if (!condition.operator || !field?.type) return;

  const fetchOperator = async () => {
    try {
      const response = await axiosInstance.get(`/operators/${field.type}`);
      const foundOperator = response.data.find(o => o.id === condition.operator);
      setOperator(foundOperator || null);
    } catch (error) {
      console.error('Error fetching operator details:', error);
      setOperator(null);
    }
  };

  fetchOperator();
}, [field?.type, condition.operator, axiosInstance]);

  // Safety check for required props - moved after hooks
  if (!condition || !field || !group || !updateConditionInGroup) {
    // // console.error('ValueField: Missing required props');
    return (
      <TextField
        fullWidth
        size="small"
        label="Value"
        value=""
        disabled
        sx={{
          '& .MuiInputBase-root': {
            height: '40px'
          }
        }}
      />
    );
  }

  // Don't show value field for operators like "is empty" or "is not empty"
  if (operator && (operator.id === 'is_empty' || operator.id === 'is_not_empty')) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        height: '40px',
        color: 'text.secondary',
        fontStyle: 'italic'
      }}>
        No value required
      </Box>
    );
  }

  // Handle echo-specific value fields
  if (field?.type === 'echo') {
    // // console.log('Echo field options:', field.options);

    if (condition.field === 'object_type') {
      const options = Array.isArray(field.options) ? field.options : [];
      return (
        <FormControl fullWidth size="small">
          {/* <InputLabel>Object Type</InputLabel>
          <Select
            value={condition.value || ''}
            onChange={(e) => updateConditionInGroup(group.id, condition.id, 'value', e.target.value)}
            label="Object Type"
            sx={{
              height: '40px',
              '& .MuiSelect-select': {
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 14px'
              }
            }}
          >
            {options.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select> */}
        </FormControl>
      );
    } else if (condition.field === 'mentions' || condition.field === 'user_mentions' || condition.field === 'record_mentions') {
      const options = Array.isArray(field.options) ? field.options : [];
      return (
        <FormControl fullWidth size="small">
          <InputLabel>Mention Type</InputLabel>
          <Select
            value={condition.value || ''}
            onChange={(e) => updateConditionInGroup(group.id, condition.id, 'value', e.target.value)}
            label="Mention Type"
            sx={{
              height: '40px',
              '& .MuiSelect-select': {
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 14px'
              }
            }}
          >
            {options.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    } else if (condition.field === 'body') {
      return (
        <TextField
          fullWidth
          size="small"
          label="Text to search"
          value={condition.value || ''}
          onChange={(e) => updateConditionInGroup(group.id, condition.id, 'value', e.target.value)}
          placeholder="Enter text to search in message body"
          sx={{
            '& .MuiInputBase-root': {
              height: '40px'
            }
          }}
        />
      );
    } else if (operator?.id === 'mention_count') {
      return (
        <TextField
          fullWidth
          size="small"
          label="Count"
          type="number"
          value={condition.value || ''}
          onChange={(e) => updateConditionInGroup(group.id, condition.id, 'value', e.target.value)}
          placeholder="Enter number of mentions"
          sx={{
            '& .MuiInputBase-root': {
              height: '40px'
            }
          }}
        />
      );
    }
  }

    // Currency field ke liye picklist dropdown


if (field?.type === 'picklist') {
  // Prepare options array
  let options = [];
  try {
    if (getPicklistOptions) {
      const picklistOptions = getPicklistOptions(field.id, field);
      options = Array.isArray(picklistOptions) ? picklistOptions : [];
    } else if (field.options) {
      options = Array.isArray(field.options) ? field.options : [];
    }
  } catch (error) {
    options = [];
  }

  return (
    <Autocomplete
      freeSolo
      options={options}
      getOptionLabel={(option) => {
        // Handle both string and object options
        return typeof option === 'string' ? option : option.label || '';
      }}
      isOptionEqualToValue={(option, value) => {
        // Compare properly for both strings and objects
        if (!value) return false;
        if (typeof option === 'string') return option === value;
        if (typeof value === 'string') return option.value === value;
        return option.value === value.value;
      }}
      value={condition.value || ''}
      onChange={(_, newValue) =>
        updateConditionInGroup(group.id, condition.id, 'value', newValue)
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Value"
          size="small"
        />
      )}
    />
  );
} 
  if (field?.type === 'reference' && referenceMap[field.id]) {
    // Handle reference fields (account_id, contact_id, etc.)
    return (
      <ReferenceAutocomplete
        field={field}
        value={condition.value || ''}
        onChange={(value) => updateConditionInGroup(group.id, condition.id, 'value', value)}
        actionIndex={null} // No action index for conditions
        onOpenCreateModal={null} // No modal creation for conditions
        referenceMap={referenceMap}
        referenceDataCache={referenceDataCache}
        referenceDataLoading={referenceDataLoading}
        fetchReferenceData={fetchReferenceData}
        prettify={prettify}
        isCreatingRecord={isCreatingRecord}
      />
    );
  } else if (field?.type === 'date' || field?.type === 'datetime' || (field && isDateTimeField(field))) {
    // Handle date fields with RelativeDateInput
    return (
      <RelativeDateInput
        label="Value"
        value={condition.value || ''}
        onChange={(value) => updateConditionInGroup(group.id, condition.id, 'value', value)}
      />
    );
  } else {
    return (
      <TextField
        fullWidth
        size="small"
        label="Value"
        value={condition.value}
        onChange={(e) => updateConditionInGroup(group.id, condition.id, 'value', e.target.value)}
        type={field?.type === 'number' || field?.type === 'currency' ? 'number' : 'text'}
        sx={{
          '& .MuiInputBase-root': {
            height: '40px'
          }
        }}
      />
    );
  }
}

const AutomationWizard = React.memo(({ open, automation, initialData, onClose, onSave }) => {
  
  const { user, axiosInstance } = useAuth();
  const { showTravel, loading: travelUiLoading } = useTravelUi();
  const [activeStep, setActiveStep] = useState(0);
  const [wizardData, setWizardData] = useState({
    name: '',
    object: '',
    trigger: '',
    conditions: [],
    actions: [],
    isActive: true
  });
  const [errors, setErrors] = useState({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [conditionLogic, setConditionLogic] = useState('AND');
  const [conditionGroups, setConditionGroups] = useState([]);
  const [groupLogic, setGroupLogic] = useState('AND');
  const [objectFields, setObjectFields] = useState({});
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [availableObjects, setAvailableObjects] = useState([]);
  const [relatedObjects, setRelatedObjects] = useState([]);
  const [createRecordFields, setCreateRecordFields] = useState([]);
  const [relatedRecordFields, setRelatedRecordFields] = useState([]);
  const [referenceAutocomplete, setReferenceAutocomplete] = useState({}); // { fieldId: { options: [], loading: false, input: '' } }
  const debounceTimeouts = useRef({});
  const [productMeta, setProductMeta] = useState({ categories: [], brands: [], manufacturers: [], locations: [] });
  const [selectedCreateFields, setSelectedCreateFields] = useState({});
  const [configuredWebhooks, setConfiguredWebhooks] = useState([]);

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [isLoadingEmailTemplates, setIsLoadingEmailTemplates] = useState(false);

  // Template preview state
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);

  // Content preview state
  const [previewContent, setPreviewContent] = useState('');
  const [openContentPreviewDialog, setOpenContentPreviewDialog] = useState(false);

  // Field copy state
  const [copiedFieldCode, setCopiedFieldCode] = useState('');

  // Snackbar state for copy notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Global cache for reference data to prevent repeated API calls
  const [referenceDataCache, setReferenceDataCache] = useState({});
  const [referenceDataLoading, setReferenceDataLoading] = useState({});

  // Prefill from template initialData when creating new (not editing)
  useEffect(() => {
    if (open && !automation && initialData) {
      const safe = {
        name: initialData.name || '',
        object: initialData.object || '',
        trigger: initialData.trigger || '',
        conditions: Array.isArray(initialData.conditions) ? initialData.conditions : [],
        actions: Array.isArray(initialData.actions) ? initialData.actions : [],
        isActive: typeof initialData.isActive === 'boolean' ? initialData.isActive : true
      };
      setWizardData(safe);
    }
  }, [open, automation, initialData]);

  // Modal state for creating related records
  const [modalStates, setModalStates] = useState({
    account: { open: false, data: null },
    contact: { open: false, data: null },
    lead: { open: false, data: null },
    case: { open: false, data: null },
    order: { open: false, data: null },
    quotation: { open: false, data: null }
  });

  // State for managing which field is being enhanced
  const [enhancedFieldContext, setEnhancedFieldContext] = useState({
    actionIndex: null,
    fieldId: null,
    recordType: null
  });

  // Loading state for record creation
  const [isCreatingRecord, setIsCreatingRecord] = useState(false);
  
  // State to track selected account for deal filtering
  const [selectedAccountForDeals, setSelectedAccountForDeals] = useState(null);



  const referenceMap = useMemo(() => {
    //console.log('🗺️ referenceMap recalculating - selectedAccountForDeals:', selectedAccountForDeals);
    const map = {
      account_id: { endpoint: '/accounts/', labelField: 'name' },
      contact_id: { endpoint: selectedAccountForDeals ? `/accounts/contacts/${selectedAccountForDeals}` : '/contacts/?all=true', labelField: 'first_name' },
      lead_id: { endpoint: '/leads/', labelField: 'full_lead_name' },
      deal_id: { endpoint: selectedAccountForDeals ? `/accounts/deals/${selectedAccountForDeals}` : '/deals/', labelField: 'name' },
      user_id: { endpoint: '/users/', labelField: 'fullname' },
      assigned_to: { endpoint: '/users/', labelField: 'fullname' },
      order_id: { endpoint: selectedAccountForDeals ? `/orders/account/${selectedAccountForDeals}` : '/orders/', labelField: 'order_number' },
      reports_to: { endpoint: '/contacts/?all=true', labelField: 'first_name' },
      invoice_id: { endpoint: selectedAccountForDeals ? `/invoices/?account_id=${selectedAccountForDeals}` : '/invoices/', labelField: 'invoice_number' },
      case_id: { endpoint: selectedAccountForDeals ? `/cases/account/${selectedAccountForDeals}` : '/cases/', labelField: 'case_number' },
      quotation_id: { endpoint: '/quotations/', labelField: 'quote_name' },
      product_id: { endpoint: '/products/', labelField: 'name' },
      created_by: { endpoint: '/users/', labelField: 'fullname' },
      updated_by: { endpoint: '/users/', labelField: 'fullname' },
      manager_id: { endpoint: '/users/', labelField: 'fullname' },
      salesperson_id: { endpoint: '/users/', labelField: 'fullname' },
      owner_id: { endpoint: '/users/', labelField: 'fullname' }
    };
    return map;
  }, [selectedAccountForDeals]);

  const prettify = (id) => id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  // Create Record type picker — separate from relatedObjects (shared with Update Related).
  // Preserve saved legacy targets when travel is on so existing automations still edit.
  const createRecordTypeOptions = useMemo(() => {
    let ids = filterAutomationObjectIds(
      ['account', 'contact', 'lead', 'deal', 'case', 'order', 'quotation', 'product'],
      showTravel,
    );
    for (const action of wizardData.actions || []) {
      if (action.type !== 'Create Record') continue;
      const recordType = action.parameters?.recordType;
      if (
        LEGACY_CREATE_RECORD_TYPES.includes(recordType) &&
        !ids.includes(recordType)
      ) {
        ids = [...ids, recordType];
      }
    }
    return ids;
  }, [showTravel, wizardData.actions]);

  // Add effect to fetch available objects (backend also filters by travel toggle).
  // Keep the trigger object of an automation being edited even if travel-hidden
  // (backend /objects omits those ids when travel is on).
  useEffect(() => {
    const fetchObjects = async () => {
      try {
        const response = await axiosInstance.get('/objects');
        const objects = Array.isArray(response.data) ? response.data : [];
        const editingTrigger = automation?.trigger_object || automation?.object || '';
        let filtered = objects.filter((obj) => {
          const id = obj?.id;
          if (editingTrigger && id === editingTrigger) return true;
          if (showTravel && LEGACY_CRM_AUTOMATION_OBJECTS.has(id)) return false;
          if (!showTravel && TRAVEL_AUTOMATION_OBJECTS.has(id)) return false;
          return true;
        });
        if (
          editingTrigger &&
          !filtered.some((obj) => obj?.id === editingTrigger)
        ) {
          filtered = [
            ...filtered,
            {
              id: editingTrigger,
              name: prettify(editingTrigger),
              type: 'standard',
              is_custom: false,
              supports_automation: true,
            },
          ];
        }
        setAvailableObjects(filtered);
      } catch (error) {
      }
    };
    fetchObjects();
  }, [axiosInstance, showTravel, automation?.trigger_object, automation?.object]);

  // Add effect to fetch email templates
  useEffect(() => {
    const fetchEmailTemplates = async () => {
      try {
        setIsLoadingEmailTemplates(true);
        const response = await axiosInstance.get('/templates/email');
        setEmailTemplates(response.data);
      } catch (error) {
        console.error('Error fetching email templates:', error);
        // Set empty array if templates endpoint is not available
        setEmailTemplates([]);
      } finally {
        setIsLoadingEmailTemplates(false);
      }
    };
    fetchEmailTemplates();
  }, []);

  // Load existing automation data when editing
  useEffect(() => {
    if (automation && automation.id && open) {
      const deserializeDateValueForLoad = (fieldDef, value) => {
        if (!fieldDef || (fieldDef.type !== 'date' && fieldDef.type !== 'datetime')) return value;
        if (typeof value === 'string' && value.startsWith('RELATIVE::')) {
          try {
            const parts = value.split('::');
            if (parts.length === 3) {
              return {
                mode: 'relative',
                amount: parseInt(parts[1]) || 0,
                unit: parts[2] || 'days'
              };
            }
          } catch (e) {
            console.warn('Failed to deserialize relative date:', value, e);
          }
        }
        return value;
      };

      const newWizardData = {
        name: automation.name || '',
        object: automation.trigger_object || '',
        trigger: automation.trigger_event || '',
        conditions: [], // We'll use conditionGroups instead
        actions: automation.actions ? (Array.isArray(automation.actions) ? automation.actions : [automation.actions]) : [],
        isActive: automation.is_active !== undefined ? automation.is_active : true
      };
      
      // Validate trigger compatibility with object type
      if ((newWizardData.object === 'invoice' || newWizardData.object === 'order') && newWizardData.trigger === 'on delete') {
        // Show warning for invalid trigger combinations
        setSnackbar({ 
          open: true, 
          message: 'Warning: This automation has an invalid trigger combination. Invoices and orders do not support deletion.', 
          severity: 'warning' 
        });
        // Clear the invalid trigger
        newWizardData.trigger = '';
      }
      
      // Deserialize relative date values in actions
      if (newWizardData.actions && newWizardData.actions.length > 0) {
        newWizardData.actions = newWizardData.actions.map((action, index) => {
          const processedAction = { ...action };
          
          // Process Update Field action
          if (processedAction.type === 'Update Field' && processedAction.parameters?.field) {
            // Check if the value looks like a relative date string
            if (typeof processedAction.parameters.value === 'string' && 
                processedAction.parameters.value.startsWith('RELATIVE::')) {
              // console.log(`=== DEBUG: Found relative date string in Update Field action: ${processedAction.parameters.value} ===`);
              processedAction.parameters.value = deserializeDateValueForLoad(
                { type: 'date' }, 
                processedAction.parameters.value
              );
              // console.log(`=== DEBUG: Deserialized to: ${JSON.stringify(processedAction.parameters.value)} ===`);
            }
          }
          
          // Process Create Record action
          if (processedAction.type === 'Create Record' && processedAction.parameters) {
            Object.keys(processedAction.parameters).forEach(key => {
              if (key !== 'recordType' && typeof processedAction.parameters[key] === 'string' && 
                  processedAction.parameters[key].startsWith('RELATIVE::')) {
                // console.log(`=== DEBUG: Found relative date string in Create Record action field ${key}: ${processedAction.parameters[key]} ===`);
                processedAction.parameters[key] = deserializeDateValueForLoad(
                  { type: 'date' }, 
                  processedAction.parameters[key]
                );
                // console.log(`=== DEBUG: Deserialized ${key} to: ${JSON.stringify(processedAction.parameters[key])} ===`);
              }
            });
          }
          
          // Process Update Related Record action
          if (processedAction.type === 'Update Related Record' && processedAction.parameters?.relatedValue) {
            if (typeof processedAction.parameters.relatedValue === 'string' && 
                processedAction.parameters.relatedValue.startsWith('RELATIVE::')) {
              // console.log(`=== DEBUG: Found relative date string in Update Related Record action: ${processedAction.parameters.relatedValue} ===`);
              processedAction.parameters.relatedValue = deserializeDateValueForLoad(
                { type: 'date' }, 
                processedAction.parameters.relatedValue
              );
              // console.log(`=== DEBUG: Deserialized relatedValue to: ${JSON.stringify(processedAction.parameters.relatedValue)} ===`);
            }
          }
          
          // console.log(`=== DEBUG: Action ${index} after deserialization ===`, processedAction);
          return processedAction;
        });
      }
      
      // Ensure each action has the proper timeDelay structure
      if (newWizardData.actions && newWizardData.actions.length > 0) {
        newWizardData.actions = newWizardData.actions.map((action, index) => {
          // Ensure timeDelay structure exists
          const defaultTimeDelay = {
            enabled: false,
            days: 0,
            hours: 0,
            minutes: 0
          };
          
          const actionWithTimeDelay = {
            ...action,
            timeDelay: action.timeDelay || defaultTimeDelay
          };
          
          // Ensure timeDelay has all required properties
          if (actionWithTimeDelay.timeDelay) {
            actionWithTimeDelay.timeDelay = {
              ...defaultTimeDelay,
              ...actionWithTimeDelay.timeDelay
            };
          }
          
          return actionWithTimeDelay;
        });
      }

      // Check if trigger event needs to be mapped
      let mappedTrigger = automation.trigger_event;
      if (automation.trigger_event) {
        // Map backend trigger values to frontend trigger IDs
        const triggerMapping = {
          'created': 'on create',
          'updated': 'on update',
          'deleted': 'on delete',
          'create': 'on create',
          'update': 'on update',
          'delete': 'on delete',
          'on create': 'on create',
          'on update': 'on update',
          'on delete': 'on delete'
        };
        mappedTrigger = triggerMapping[automation.trigger_event.toLowerCase()] || automation.trigger_event;
        // // console.log('Mapped trigger event:', mappedTrigger);
      }

      const finalWizardData = {
        ...newWizardData,
        trigger: mappedTrigger
      };
      // // console.log('Final wizard data:', finalWizardData);

      // Add a small delay to ensure availableObjects are loaded
      setTimeout(() => {
        setWizardData(finalWizardData);
      }, 100);

      // Load condition groups if they exist
      if (automation.trigger_conditions && Array.isArray(automation.trigger_conditions)) {
        // console.log('Setting condition groups from array:', automation.trigger_conditions);
        // Convert old timestamp-based IDs to simple sequential IDs
        const convertedGroups = automation.trigger_conditions.map((group, groupIndex) => ({
          ...group,
          id: `group_${groupIndex + 1}`,
          name: `Group ${groupIndex + 1}`,
          conditions: group.conditions ? group.conditions.map((condition, conditionIndex) => ({
            ...condition,
            id: `condition_${conditionIndex + 1}`
          })) : []
        }));
        // console.log('Converted condition groups:', convertedGroups);
        setConditionGroups(convertedGroups);
        if (automation.group_logic) {
          // console.log('=== DEBUG: Setting groupLogic to ===', automation.group_logic);
          setGroupLogic(automation.group_logic);
        } else {
          // console.log('=== DEBUG: No group_logic found, keeping default ===', groupLogic);
        }
      } else if (automation.trigger_conditions && typeof automation.trigger_conditions === 'object') {
        // Convert old format to new condition groups format
        const convertedGroups = [{
          id: 'group_1',
          name: 'Group 1',
          logic: 'AND',
          logicMode: 'simple',
          customLogic: '',
          conditions: Array.isArray(automation.trigger_conditions) ? automation.trigger_conditions.map((condition, index) => ({
            ...condition,
            id: `condition_${index + 1}`
          })) : []
        }];
        // console.log('Converting to condition groups:', convertedGroups);
        setConditionGroups(convertedGroups);
      } else {
        // console.log('No trigger conditions found, setting empty array');
        setConditionGroups([]);
      }
    }
  }, [automation, open]);

  // Fetch configured webhooks
  useEffect(() => {
    const fetchWebhooks = async () => {
      try {
        const response = await axiosInstance.get('/company-api-keys/info');
        if (response.data && response.data.webhooks) {
          setConfiguredWebhooks(response.data.webhooks.filter(webhook => webhook.isActive));
        }
      } catch (error) {
        // console.error('Error fetching webhooks:', error);
      }
    };
    fetchWebhooks();
  }, []);

  // Reset wizard data when opening for new automation
  useEffect(() => {
    if (open && !automation) {
      // console.log('Resetting wizard data for new automation');
      setWizardData({
        name: '',
        object: '',
        trigger: '',
        conditions: [],
        actions: [],
        isActive: true
      });
      setConditionGroups([]);
      setActiveStep(0);
      setErrors({});
      // Reset groupLogic to default when creating new automation
      setGroupLogic('AND');
    }
  }, [open, automation]);

  // Note: Custom fields are now included in the /fields/{object} endpoint
  // So we don't need to fetch them separately
  // Custom fields will be available in objectFields with is_custom: true flag

  // Helper function to convert singular object names to plural table names for custom fields
  const getTableNameForCustomFields = (objectName) => {
    const mapping = {
      'lead': 'leads',
      'contact': 'contacts',
      'deal': 'deals',
      'account': 'accounts',
      'order': 'orders',
      'quotation': 'quotations',
      'product': 'products',
      'invoice': 'invoices',
      'case': 'cases'
    };
    return mapping[objectName] || objectName;
  };

  // Function to fetch related object fields for email functionality
  const fetchRelatedObjectFields = async (mainObject) => {
    const relatedObjects = getRelatedObjectsForEmail(mainObject);

    for (const relatedObj of relatedObjects) {
      try {
        const response = await axiosInstance.get(`/fields/${relatedObj}`);
        setObjectFields(prev => ({
          ...prev,
          [relatedObj]: response.data
        }));
      } catch (error) {
        console.error(`Error fetching fields for ${relatedObj}:`, error);
      }
    }
  };

  // Add effect to fetch fields when object changes
  useEffect(() => {
    const fetchFields = async () => {
      if (!wizardData.object) return;

      setIsLoadingFields(true);
      try {
        const response = await axiosInstance.get(`/fields/${wizardData.object}`);
        // console.log(`Fields response for ${wizardData.object}:`, response.data);

        // Check if custom fields are in the response
        const customFieldsInResponse = Object.entries(response.data || {}).filter(([key, value]) => value.is_custom);
        // console.log(`Custom fields in response for ${wizardData.object}:`, customFieldsInResponse);

        setObjectFields(prev => ({
          ...prev,
          [wizardData.object]: response.data
        }));

        // Also fetch related object fields for email functionality
        await fetchRelatedObjectFields(wizardData.object);
      } catch (error) {
      } finally {
        setIsLoadingFields(false);
      }
    };

    fetchFields();
  }, [wizardData.object]);

  // Fetch related objects when trigger object changes
  useEffect(() => {
    if (wizardData.object) {
      axiosInstance.get(`/related-objects/${wizardData.object}`)
        .then(res => setRelatedObjects(res.data))
        .catch(() => setRelatedObjects([]));
    } else {
      setRelatedObjects([]);
    }
  }, [wizardData.object]);

  // Fetch required fields for Create Record action
  useEffect(() => {
    const createAction = wizardData.actions.find(a => a.type === 'Create Record');
    if (createAction && createAction.parameters?.recordType) {
      axiosInstance.get(`/fields/${createAction.parameters.recordType}`)
        .then(res => setCreateRecordFields(
          Object.entries(res.data)
            .map(([id, def]) => ({ id, ...def }))
        ));
    } else {
      setCreateRecordFields([]);
    }
  }, [wizardData.actions]);



  // Fetch fields for Update Related Record action
  useEffect(() => {
    const updateAction = wizardData.actions.find(a => a.type === 'Update Related Record');
    if (updateAction && updateAction.parameters?.relatedRecordType) {
      axiosInstance.get(`/fields/${updateAction.parameters.relatedRecordType}`)
        .then(res => setRelatedRecordFields(
          Object.entries(res.data).map(([id, def]) => ({ id, ...def }))
        ));
    } else {
      setRelatedRecordFields([]);
    }
  }, [wizardData.actions]);

  // Fetch reference options for all reference fields in createRecordFields and relatedRecordFields
  useEffect(() => {
    const allReferenceFields = [
      ...createRecordFields,
      ...relatedRecordFields
    ].filter(field => field.type === 'reference' && referenceMap[field.id]);

    allReferenceFields.forEach(field => {
      if (!referenceAutocomplete[field.id]) {
        axiosInstance.get(referenceMap[field.id].endpoint)
          .then(res => setReferenceAutocomplete(prev => ({
            ...prev,
            [field.id]: {
              ...(prev[field.id] || {}),
              options: (res.data || []).map(item => ({ id: item.id, label: item[referenceMap[field.id].labelField] || item.name || item.full_name || item.full_lead_name || item.order_number })),
              loading: false,
              input: ''
            }
          })));
      }
    });
    // eslint-disable-next-line
  }, [createRecordFields, relatedRecordFields]);

  // Clear deal data cache when selected account changes
  useEffect(() => {
    if (selectedAccountForDeals !== null) {
      // Clear the deal data cache to force refetch with new account
      setReferenceDataCache(prev => ({
        ...prev,
        [`/accounts/deals/${selectedAccountForDeals}`]: []
      }));

      // Also clear other account-scoped caches so fresh data loads for the selected account
      setReferenceDataCache(prev => ({
        ...prev,
        [`/accounts/contacts/${selectedAccountForDeals}`]: [],
        [`/orders/account/${selectedAccountForDeals}`]: [],
        [`/cases/account/${selectedAccountForDeals}`]: [],
        [`/invoices/?account_id=${selectedAccountForDeals}`]: []
      }));
    }
  }, [selectedAccountForDeals]);

  useEffect(() => {
    if (automation) {
      setWizardData({
        name: automation.name || '',
        object: automation.object || '',
        trigger: automation.trigger || '',
        conditions: automation.conditions || [],
        actions: automation.actions || [],
        isActive: automation.status === 'Active'
      });
      // Initialize condition groups from existing conditions
      if (automation.conditions && automation.conditions.length > 0) {
        setConditionGroups([{
          id: 'group_1',
          name: 'Group 1',
          logic: 'AND',
          logicMode: 'simple', // 'simple' or 'advanced'
          customLogic: '',
          conditions: automation.conditions
        }]);
      }
    } else {
      setWizardData({
        name: '',
        object: '',
        trigger: '',
        conditions: [],
        actions: [],
        isActive: true
      });
      setConditionGroups([]);
    }
    setActiveStep(0);
    setErrors({});
  }, [automation, open]);

  // Fetch product meta when object is 'product'
  useEffect(() => {
    if (wizardData.object === 'product') {
      axiosInstance.get('/product-meta').then(res => {
        setProductMeta(res.data);
      }).catch(() => {
        setProductMeta({ categories: [], brands: [], manufacturers: [], locations: [] });
      });
    }
  }, [wizardData.object]);

  const handleNext = async () => {
    setShowValidationErrors(true);
    if (await validateStep(activeStep)) {
      setSnackbar({ open: false, message: '', severity: 'success' });
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setShowValidationErrors(false);
    setActiveStep(prev => prev - 1);
  };

  const handleSave = async () => {
    if (await validateStep(activeStep)) {
      try {
        // Helper: serialize relative date objects for backend persistence
        const serializeDateValueForSave = (fieldDef, value) => {
          if (!fieldDef) return value;
          if (fieldDef.type !== 'date' && fieldDef.type !== 'datetime') return value;
          if (value && typeof value === 'object' && value.mode === 'relative') {
            const amount = Number.isFinite(value.amount) ? value.amount : 0;
            const unit = value.unit || 'days';
            // Encode a backend-friendly marker string
            return `RELATIVE::${amount}::${unit}`;
          }
          return value;
        };

        // Build a quick lookup for field definitions by id for current object
        const fieldsById = Object.fromEntries(
          (getAvailableFields() || []).map(f => [f.id, f])
        );

        // Transform actions to serialize date fields appropriately
        const transformedActions = (wizardData.actions || []).map(action => {
          if (!action || !action.type) return action;
          const cloned = JSON.parse(JSON.stringify(action));

          if (cloned.type === 'Update Field') {
            const field = fieldsById[cloned.parameters?.field];
            if (field) {
              cloned.parameters.value = serializeDateValueForSave(field, cloned.parameters?.value);
            }
          } else if (cloned.type === 'Create Record') {
            // Use createRecordFields metadata to find date fields
            const defs = createRecordFields || [];
            defs.forEach(def => {
              if (!cloned.parameters) return;
              if ((def.type === 'date' || def.type === 'datetime') && def.id in cloned.parameters) {
                cloned.parameters[def.id] = serializeDateValueForSave(def, cloned.parameters[def.id]);
              }
            });
          } else if (cloned.type === 'Update Related Record') {
            const def = relatedRecordFields.find(f => f.id === cloned.parameters?.relatedField);
            if (def) {
              cloned.parameters.relatedValue = serializeDateValueForSave(def, cloned.parameters?.relatedValue);
            }
          }
          return cloned;
        });

        // Prepare automation data for database
        const automationData = {
          name: wizardData.name,
          description: wizardData.description,
          trigger_object: wizardData.object?.toLowerCase() || '',
          trigger_event: wizardData.trigger?.toLowerCase() || '',
          trigger_conditions: conditionGroups.length > 0 ? conditionGroups : wizardData.conditions,
          group_logic: groupLogic, // Save the group logic (AND/OR)
          actions: transformedActions,
          is_active: wizardData.isActive
        };

        // Call the parent's onSave function with the prepared data
        onSave && onSave(automationData);
        onClose();
      } catch (error) {
        // console.error('Error preparing automation data:', error);
        alert(`Error preparing automation data: ${error.message}`);
      }
    }
  };

  const validateStep = async (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Trigger
        if (!wizardData.name.trim()) newErrors.name = 'Name is required';
        if (!wizardData.object) newErrors.object = 'Object is required';
        if (!wizardData.trigger) newErrors.trigger = 'Trigger is required';
        
        // Validate that invoice and order objects don't use "on delete" trigger
        if ((wizardData.object === 'invoice' || wizardData.object === 'order') && wizardData.trigger === 'on delete') {
          newErrors.trigger = 'Invoices and orders do not support deletion';
          setSnackbar({ open: true, message: 'Invoices and orders do not support deletion.', severity: 'error' });
        }
        
        if (Object.keys(newErrors).length > 0) {
          setSnackbar({ open: true, message: 'Please fill all required trigger fields.', severity: 'error' });
        }
        break;
      case 1: // Conditions
        // Conditions are now optional - only validate if groups exist
        if (conditionGroups && conditionGroups.length > 0) {
          for (let groupIndex = 0; groupIndex < conditionGroups.length; groupIndex++) {
            const group = conditionGroups[groupIndex];

            // Only validate conditions if the group has conditions
            if (group.conditions && group.conditions.length > 0) {
              if (group.logicMode === 'advanced' && group.conditions.length > 1) {
                const validation = validateCustomLogic(group.customLogic, group.conditions.length);
                if (!validation.valid) {
                  newErrors[`group_${groupIndex}_logic`] = validation.error;
                  setSnackbar({ open: true, message: `Group ${groupIndex + 1}: ${validation.error}`, severity: 'error' });
                  setErrors(newErrors);
                  return false;
                }
              }

              for (let condIndex = 0; condIndex < group.conditions.length; condIndex++) {
                const condition = group.conditions[condIndex];

                // IMPORTANT: check for null/undefined explicitly for field and operator
                if (!condition.field) {
                  // skip validation if field itself missing? or treat as error? 
                  // (usually if no field, you might want to skip)
                  newErrors[`group_${groupIndex}_condition_${condIndex}field`] = 'field is required';
                  setSnackbar({
                    open: true,
                    message: "Field is required.",
                    severity: 'error'
                  });
                  setErrors(newErrors);
                  return false;
                }

                if (!condition.operator) {
                  newErrors[`group_${groupIndex}_condition_${condIndex}_operator`] = 'Operator is required';
                  setSnackbar({
                    open: true,
                    message: "Operator is required.",
                    severity: 'error'
                  });
                  setErrors(newErrors);
                  return false;
                }

                const val = condition.value;
                const operator = condition.operator;

                if (
                  val === undefined ||
                  val === null ||
                  val === '' && !['is_empty', 'is_not_empty'].includes(operator)
                ) {
                  newErrors[`group_${groupIndex}_condition_${condIndex}_value`] = 'Value is required';
                  setSnackbar({
                    open: true,
                    message: "Value is required.",
                    severity: 'error'
                  });
                  setErrors(newErrors);
                  return false;
                }
              }
            }
          }
        }
        break;
      case 2: // Actions
        if (wizardData.actions.length === 0) {
          setSnackbar({ open: true, message: 'At least one action is required.', severity: 'error' });

          newErrors.actions = 'At least one action is required';
        };

        // Validate all actions
        for (let index = 0; index < wizardData.actions.length; index++) {
          const action = wizardData.actions[index];
          if (!action.type) {
            setSnackbar({ open: true, message: 'Action type is required.', severity: 'error' });

            newErrors[`action_${index}_type`] = 'Action type is required';
          } else {
            // Validate action parameters based on type
            if (action.type === 'Create Task') {
              if (!action.parameters?.subject) {
                setSnackbar({ open: true, message: 'Title is required.', severity: 'error' });

                newErrors[`action_${index}_subject`] = 'Subject is required';
              }
              if (!action.parameters?.related_type) {
                setSnackbar({ open: true, message: 'Related To is required.', severity: 'error' });

                newErrors[`action_${index}_related_type`] = 'Related To is required';
              }
              if (action.parameters?.related_type && !action.parameters?.related_id) {
                setSnackbar({ open: true, message: 'Related Entity is required.', severity: 'error' });

                newErrors[`action_${index}_related_id`] = 'Related Entity is required';
              }
            }
            if (action.type === 'Send Email') {
              if (!action.parameters?.template) {
                setSnackbar({ open: true, message: 'Template is required.', severity: 'error' });

                newErrors[`action_${index}_template`] = 'Template is required';
              }
              if (!action.parameters?.toEmail) {
                setSnackbar({ open: true, message: 'To Email is required.', severity: 'error' });

                newErrors[`action_${index}_toEmail`] = 'To Email is required';
              }
              if (!action.parameters?.content) {
                newErrors[`action_${index}_content`] = 'Email content is required';
              }
            }
            if (action.type === 'Update Field') {
              if (!action.parameters?.field) {
                setSnackbar({ open: true, message: 'Field is required.', severity: 'error' });

                newErrors[`action_${index}_field`] = 'Field is required';
              }
              if (!action.parameters?.value) {
                setSnackbar({ open: true, message: 'Value is required.', severity: 'error' });

                newErrors[`action_${index}_value`] = 'Value is required';
              }
            }
            if (action.type === 'Create Record') {
              if (!action.parameters?.recordType) {
                setSnackbar({ open: true, message: 'Record type is required.', severity: 'error' });

                newErrors[`action_${index}_recordType`] = 'Record type is required';
              } else {
                const recordFields = createRecordFields;

                recordFields.forEach(field => {
                  const isDeal = action.parameters.recordType === 'deal';
                  const isRequired = field.required || (isDeal && ['account_id', 'contact_id'].includes(field.id));
                  const value = action.parameters?.[field.id];

                  // Check if this is an auto-generated field
                  const isAutoGeneratedField = (
                    (action.parameters?.recordType === 'quotation' && field.id === 'quote_name') ||
                    (action.parameters?.recordType === 'order' && field.id === 'order_number') ||
                    (action.parameters?.recordType === 'invoice' && field.id === 'invoice_number')
                  );

                  if (isRequired && (value === undefined || value === null || value === '') && !isAutoGeneratedField) {
                    newErrors[`action_${index}_${field.id}`] = `${field.label || field.id} is required`;
                  }
                });
              }
              const hasErrors = Object.keys(newErrors).length > 0;
              if (hasErrors) {
                // Create a detailed error message listing the missing fields
                const missingFields = [];
                createRecordFields.forEach(field => {
                  const isDeal = action.parameters.recordType === 'deal';
                  const isRequired = field.required || (isDeal && ['account_id', 'contact_id'].includes(field.id));
                  const value = action.parameters?.[field.id];
                  
                  // Check if this is an auto-generated field
                  const isAutoGeneratedField = (
                    (action.parameters?.recordType === 'quotation' && field.id === 'quote_name') ||
                    (action.parameters?.recordType === 'order' && field.id === 'order_number') ||
                    (action.parameters?.recordType === 'invoice' && field.id === 'invoice_number')
                  );
                  
                  if (isRequired && (value === undefined || value === null || value === '') && !isAutoGeneratedField) {
                    missingFields.push(field.label || field.id);
                  }
                });
                
                const errorMessage = missingFields.length > 0 
                  ? `Please fill the following required fields: ${missingFields.join(', ')}`
                  : 'Please fill all required fields.';
                  
                setSnackbar({ open: true, message: errorMessage, severity: 'error' });
              }
              return !hasErrors;
            }

            if (action.type === 'Update Related Record') {
              if (!action.parameters?.relatedRecordType) {
                setSnackbar({ open: true, message: 'Related Record Type is required.', severity: 'error' });
                newErrors[`action_${index}_relatedRecordType`] = 'Related Record Type is required';
              }
              if (!action.parameters?.relatedField) {
                setSnackbar({ open: true, message: 'Field is required.', severity: 'error' });
                newErrors[`action_${index}_relatedField`] = 'Field is required';
              }
              if (!action.parameters?.relatedValue) {
                setSnackbar({ open: true, message: 'New Value is required.', severity: 'error' });
                newErrors[`action_${index}_relatedValue`] = 'New Value is required';
              }
            }

            if (action.type === 'Webhook') {
              if (!action.parameters?.webhookId) {
                setSnackbar({ open: true, message: 'Please select a webhook.', severity: 'error' });

                newErrors[`action_${index}_webhook`] = 'Please select a webhook';
              }
            }

            // Validate time delay if enabled
            if (action.timeDelay?.enabled) {
              const totalDelay = (action.timeDelay.days || 0) + (action.timeDelay.hours || 0) + (action.timeDelay.minutes || 0);
              if (totalDelay === 0) {
                newErrors[`action_${index}_timeDelay`] = 'Please specify a delay time when time delay is enabled';
              } else {
                // Validate time delay configuration via API
                const validation = await validateTimeDelay(action.timeDelay);
                if (!validation.valid) {
                  newErrors[`action_${index}_timeDelay`] = validation.error;
                }
              }
            }
          }
        }
        break;
      case 3: // Review
        // Final validation
        if (!wizardData.name.trim()) newErrors.name = 'Name is required';
        if (!wizardData.object) newErrors.object = 'Object is required';
        if (!wizardData.trigger) newErrors.trigger = 'Trigger is required';
        if (wizardData.actions.length === 0) newErrors.actions = 'At least one action is required';

        // Validate all actions
        for (let index = 0; index < wizardData.actions.length; index++) {
          const action = wizardData.actions[index];
          if (!action.type) {
            newErrors[`action_${index}_type`] = 'Action type is required';
          } else {
            // Validate action parameters based on type
            if (action.type === 'Create Task') {
              if (!action.parameters?.subject) {
                newErrors[`action_${index}_subject`] = 'Subject is required';
              }
              if (!action.parameters?.related_type) {
                newErrors[`action_${index}_related_type`] = 'Related To is required';
              }
              if (action.parameters?.related_type && !action.parameters?.related_id) {
                newErrors[`action_${index}_related_id`] = 'Related Entity is required';
              }
            }
            if (action.type === 'Send Email') {
              if (!action.parameters?.template) {
                newErrors[`action_${index}_template`] = 'Template is required';
              }
              if (!action.parameters?.toEmail) {
                newErrors[`action_${index}_toEmail`] = 'To Email is required';
              }
              if (!action.parameters?.content) {
                newErrors[`action_${index}_content`] = 'Email content is required';
              }
            }
            if (action.type === 'Update Field') {
              if (!action.parameters?.field) {
                newErrors[`action_${index}_field`] = 'Field is required';
              }
              if (!action.parameters?.value) {
                newErrors[`action_${index}_value`] = 'Value is required';
              }
            }
            if (action.type === 'Create Record') {
              if (!action.parameters?.recordType) {
                newErrors[`action_${index}_recordType`] = 'Record type is required';
              }
            }
            if (action.type === 'Update Related Record') {
              if (!action.parameters?.relatedRecordType) {
                newErrors[`action_${index}_relatedRecordType`] = 'Related record type is required';
              }
              if (!action.parameters?.relatedField) {
                newErrors[`action_${index}_relatedField`] = 'Field is required';
              }
              if (!action.parameters?.relatedValue) {
                newErrors[`action_${index}_relatedValue`] = 'New Value is required';
              }
            }
            if (action.type === 'Webhook') {
              if (!action.parameters?.webhookId) {
                newErrors[`action_${index}_webhook`] = 'Please select a webhook';
              }
            }

            // Validate time delay if enabled
            if (action.timeDelay?.enabled) {
              const totalDelay = (action.timeDelay.days || 0) + (action.timeDelay.hours || 0) + (action.timeDelay.minutes || 0);
              if (totalDelay === 0) {
                newErrors[`action_${index}_timeDelay`] = 'Please specify a delay time when time delay is enabled';
              } else {
                // Validate time delay configuration via API
                const validation = await validateTimeDelay(action.timeDelay);
                if (!validation.valid) {
                  newErrors[`action_${index}_timeDelay`] = validation.error;
                }
              }
            }
          }
        }

        // Validate all condition groups
        conditionGroups.forEach((group, groupIndex) => {
          // Validate advanced logic if in advanced mode
          if (group.logicMode === 'advanced' && group.conditions.length > 1) {
            const validation = validateCustomLogic(group.customLogic, group.conditions.length);
            if (!validation.valid) {
              newErrors[`group_${groupIndex}_logic`] = validation.error;
            }
          }

          // Validate individual conditions
          group.conditions.forEach((condition, condIndex) => {
            if (condition.field && !condition.operator) {
              newErrors[`group_${groupIndex}_condition_${condIndex}_operator`] = 'Operator is required';
            }
            if (condition.field && condition.operator && !condition.value &&
              !['is_empty', 'is_not_empty'].includes(condition.operator)) {
              newErrors[`group_${groupIndex}_condition_${condIndex}_value`] = 'Value is required';
            }
          });
        });
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  const updateWizardData = (field, value) => {
    // console.log(`Updating wizard data: ${field} = ${value}`);
    
    // Special handling for object changes
    if (field === 'object') {
      setWizardData(prev => {
        const newData = { ...prev, [field]: value };
        
        // If switching to invoice or order and current trigger is "on delete", clear the trigger
        if ((value === 'invoice' || value === 'order') && prev.trigger === 'on delete') {
          newData.trigger = '';
          // Show a message explaining why the trigger was cleared
          setSnackbar({ 
            open: true, 
            message: 'Trigger cleared: Invoices and orders do not support deletion.', 
            severity: 'info' 
          });
        }
        
        return newData;
      });
      
      // Clear trigger validation errors when object changes
      if (errors.trigger) {
        setErrors(prev => ({ ...prev, trigger: undefined }));
      }
    } else {
      setWizardData(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear related errors
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const addAction = () => {
    const newAction = {
      type: '',
      parameters: {},
      timeDelay: {
        enabled: false,
        days: 0,
        hours: 0,
        minutes: 0
      }
    };
    setWizardData(prev => ({
      ...prev,
      actions: [...prev.actions, newAction]
    }));
  };

  const updateAction = (index, field, value) => {
    const newActions = [...wizardData.actions];
    if (field === 'type') {
      // Reset parameters when action type changes
      newActions[index] = {
        type: value,
        parameters: {},
        timeDelay: {
          enabled: false,
          days: 0,
          hours: 0,
          minutes: 0
        }
      };
    } else if (field === 'parameters') {
      // Handle parameters object updates
      newActions[index] = {
        ...newActions[index],
        parameters: value
      };

    } else if (field === 'timeDelay') {
      // Handle time delay updates
      newActions[index] = {
        ...newActions[index],
        timeDelay: value
      };
    } else if (field.startsWith('timeDelay.')) {
      // Handle individual time delay field updates
      const timeDelayField = field.split('.')[1];
      newActions[index] = {
        ...newActions[index],
        timeDelay: {
          ...newActions[index].timeDelay,
          [timeDelayField]: value
        }
      };

    } else {
      // Handle individual parameter updates
      newActions[index] = {
        ...newActions[index],
        parameters: {
          ...newActions[index].parameters,
          [field]: value
        }
      };
    }
    
    setWizardData(prev => ({ ...prev, actions: newActions }));

    // Clear related errors when field is updated
    const errorKey = `action_${index}_${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: undefined }));
    }
  };

  const removeAction = (index) => {
    setWizardData(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }));
  };



  // Modify getAvailableFields to use the fetched fields
  const getAvailableFields = () => {
    if (!wizardData.object) return [];

    const fields = objectFields[wizardData.object] || {};
    // console.log(`Fields for ${wizardData.object}:`, fields);

    const allFields = Object.entries(fields)
      .filter(([fieldName]) => !AUDIT_FIELDS.includes(fieldName))
      .map(([fieldName, fieldDef]) => {
        const field = {
          id: fieldName,
          name: fieldName,
          label: fieldDef.label || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          type: fieldDef.type,
          required: fieldDef.required || false,
          options: fieldDef.options || [],
          reference_to: fieldDef.reference_to,
          is_custom: fieldDef.is_custom || false
        };
        
        // Override type to datetime for specific field names
        if (isDateTimeField(field)) {
          field.type = 'datetime';
        }
        
        return field;
      });

    // Debug logging
    const customFields = allFields.filter(field => field.is_custom);
    return allFields;
  };

  // Fields available specifically for Conditions UI
  const getAvailableFieldsForConditions = () => {
    const fields = getAvailableFields();
    // When object is echo, hide object_type from Conditions
    if (wizardData.object === 'echo') {
      return fields.filter(f => f.id !== 'object_type');
    }
    return fields;
  };

  const getAvailableFieldsForEmail = () => {
    if (!wizardData.object) return [];

    const baseFields = [
      { id: 'created_at', label: 'Created Date', code: '{{created_at}}' },
      { id: 'updated_at', label: 'Updated Date', code: '{{updated_at}}' },
      { id: 'created_by_name', label: 'Created By', code: '{{created_by_name}}' },
      { id: 'updated_by_name', label: 'Updated By', code: '{{updated_by_name}}' }
    ];

    // Get dynamic fields from metadata API
    const dynamicFields = getDynamicFieldsForEmail(wizardData.object);

    // Get related module fields
    const relatedFields = getRelatedModuleFields(wizardData.object);

    return [...baseFields, ...dynamicFields, ...relatedFields];
  };

  const getDynamicFieldsForEmail = (objectType) => {
    if (!objectType || !objectFields[objectType]) return [];

    const fields = objectFields[objectType];
    const emailFields = [];

    // Convert object fields to email field format
    Object.entries(fields).forEach(([fieldName, fieldDef]) => {
      // Skip audit fields and reference fields for email
      if (!fieldName || !fieldDef || AUDIT_FIELDS.includes(fieldName) || fieldDef.type === 'reference') {
        return;
      }

      try {
        emailFields.push({
          id: fieldName,
          label: fieldDef.label || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          code: `{{${fieldName}}}`,
          type: fieldDef.type,
          is_custom: fieldDef.is_custom || false
        });
      } catch (error) {
        console.warn(`Error processing field ${fieldName}:`, error);
      }
    });

    return emailFields;
  };

  const getRelatedModuleFields = (objectType) => {
    // Get related objects from metadata API
    const relatedObjects = getRelatedObjectsForEmail(objectType);
    const relatedFields = [];

    // For each related object, get its fields and create field codes
    relatedObjects.forEach(relatedObj => {
      if (objectFields[relatedObj]) {
        Object.entries(objectFields[relatedObj]).forEach(([fieldName, fieldDef]) => {
          // Skip audit fields and reference fields
          if (!fieldName || !fieldDef || AUDIT_FIELDS.includes(fieldName) || fieldDef.type === 'reference') {
            return;
          }

          try {
            // Create a unique identifier for related object fields
            const relatedFieldId = `${relatedObj}_${fieldName}`;
            const relatedFieldLabel = `${relatedObj.charAt(0).toUpperCase() + relatedObj.slice(1)} ${fieldDef.label || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`;

            relatedFields.push({
              id: relatedFieldId,
              label: relatedFieldLabel,
              code: `{{${relatedObj}.${fieldName}}}`,
              type: fieldDef.type,
              is_custom: fieldDef.is_custom || false,
              related_object: relatedObj
            });
          } catch (error) {
            console.warn(`Error processing related field ${relatedObj}.${fieldName}:`, error);
          }
        });
      }
    });

    return relatedFields;
  };

  const getRelatedObjectsForEmail = (objectType) => {
    // Define related objects for email field access
    const relatedObjectMap = {
      'leads': ['accounts', 'deals', 'contacts'],
      'accounts': ['leads', 'deals', 'contacts', 'orders', 'invoices'],
      'deals': ['accounts', 'leads', 'contacts'],
      'contacts': ['accounts', 'leads', 'deals'],
      'orders': ['accounts', 'contacts', 'deals'],
      'invoices': ['accounts', 'contacts', 'deals', 'orders'],
      'quotations': ['accounts', 'deals', 'orders'],
      'cases': ['accounts', 'contacts'],
      'products': ['orders', 'quotations', 'invoices']
    };

    return relatedObjectMap[objectType] || [];
  };

  const getRelatedObjectsForUpdateRelatedRecord = (objectType) => {
    // Define related objects for Update Related Record action
    const relatedObjectMap = {
      'lead': ['account', 'deal', 'contact'],
      'account': ['lead', 'deal', 'contact', 'order', 'invoice'],
      'deal': ['account', 'lead', 'contact'],
      'contact': ['account', 'lead', 'deal'],
      'order': ['account', 'contact', 'deal'],
      'invoice': ['account', 'contact', 'deal', 'order'],
      'quotation': ['account', 'deal', 'order'],
      'case': ['account', 'contact'],
      'product': ['order', 'quotation', 'invoice']
    };

    return filterAutomationObjectIds(relatedObjectMap[objectType] || [], showTravel);
  };

  const insertFieldCode = (fieldCode, actionIndex) => {
    const currentContent = wizardData.actions[actionIndex]?.parameters?.content || '';
    const newContent = currentContent + fieldCode;

    updateAction(actionIndex, 'parameters', {
      ...wizardData.actions[actionIndex]?.parameters,
      content: newContent
    });
  };

  const copyFieldCode = (fieldCode) => {
    navigator.clipboard.writeText(fieldCode).then(() => {
      setCopiedFieldCode(fieldCode);
      setSnackbar({
        open: true,
        message: 'Field code copied to clipboard!',
        severity: 'success'
      });
      // Clear the copied code after 5 seconds
      setTimeout(() => setCopiedFieldCode(''), 5000);
    }).catch(err => {
      console.error('Failed to copy field code:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fieldCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedFieldCode(fieldCode);
      setSnackbar({
        open: true,
        message: 'Field code copied to clipboard!',
        severity: 'success'
      });
      setTimeout(() => setCopiedFieldCode(''), 5000);
    });
  };

  const handleFieldSelection = (fieldCode, actionIndex) => {
    if (fieldCode) {
      // Clear any previously copied field code
      setCopiedFieldCode('');
      // Copy the new field code
      copyFieldCode(fieldCode);
    }
  };

  const handleTemplateSelection = (templateId, actionIndex) => {
    if (templateId) {
      const selectedTemplate = emailTemplates.find(t => t.id === templateId);
      if (selectedTemplate) {
        // Update the action with template content
        updateAction(actionIndex, 'parameters', {
          ...wizardData.actions[actionIndex]?.parameters,
          template: templateId,
          content: selectedTemplate.content || ''
        });
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const replaceFieldCodesWithSampleData = (content) => {
    if (!content) return '';

    let processedContent = content;

    try {
      // Generate sample data dynamically based on available fields
      const sampleData = generateSampleDataForPreview();

      // Replace all field codes with sample data
      Object.entries(sampleData).forEach(([code, value]) => {
        try {
          processedContent = processedContent.replace(new RegExp(code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
        } catch (error) {
          console.warn(`Error replacing field code ${code}:`, error);
        }
      });
    } catch (error) {
      console.error('Error generating sample data for preview:', error);
      return content; // Return original content if preview generation fails
    }

    return processedContent;
  };

  const generateSampleDataForPreview = () => {
    const sampleData = {};

    // Base audit fields
    sampleData['{{created_at}}'] = '2024-01-15 10:30:00';
    sampleData['{{updated_at}}'] = '2024-01-15 14:45:00';
    sampleData['{{created_by_name}}'] = 'Admin User';
    sampleData['{{updated_by_name}}'] = 'Admin User';

    // Generate sample data for current object fields
    if (wizardData.object && objectFields[wizardData.object]) {
      Object.entries(objectFields[wizardData.object]).forEach(([fieldName, fieldDef]) => {
        if (fieldName && fieldDef && !AUDIT_FIELDS.includes(fieldName) && fieldDef.type !== 'reference') {
          try {
            sampleData[`{{${fieldName}}}`] = generateSampleValue(fieldDef.type, fieldDef.label || fieldName);
          } catch (error) {
            console.warn(`Error generating sample value for field ${fieldName}:`, error);
            sampleData[`{{${fieldName}}}`] = 'Sample value';
          }
        }
      });
    }

    // Generate sample data for related object fields
    if (wizardData.object) {
      const relatedObjects = getRelatedObjectsForEmail(wizardData.object);
      relatedObjects.forEach(relatedObj => {
        if (objectFields[relatedObj]) {
          Object.entries(objectFields[relatedObj]).forEach(([fieldName, fieldDef]) => {
            if (fieldName && fieldDef && !AUDIT_FIELDS.includes(fieldName) && fieldDef.type !== 'reference') {
              try {
                sampleData[`{{${relatedObj}.${fieldName}}}`] = generateSampleValue(fieldDef.type, fieldDef.label || fieldName);
              } catch (error) {
                console.warn(`Error generating sample value for field ${relatedObj}.${fieldName}:`, error);
                sampleData[`{{${relatedObj}.${fieldName}}}`] = 'Sample value';
              }
            }
          });
        }
      });
    }

    return sampleData;
  };

  const generateSampleValue = (fieldType, fieldLabel) => {
    // Ensure fieldLabel is a string and convert to lowercase safely
    const fieldName = (fieldLabel || '').toString().toLowerCase();

    // Generate sample values based on field type and name
    switch (fieldType) {
      case 'text':
      case 'textarea':
        if (fieldName.includes('name')) return 'John Doe';
        if (fieldName.includes('company')) return 'Acme Corporation';
        if (fieldName.includes('description')) return 'Sample description text';
        if (fieldName.includes('address')) return '123 Business St, City, State 12345';
        if (fieldName.includes('city')) return 'New York';
        if (fieldName.includes('state')) return 'NY';
        if (fieldName.includes('country')) return 'United States';
        if (fieldName.includes('postal_code')) return '10001';
        if (fieldName.includes('website')) return 'www.example.com';
        if (fieldName.includes('notes')) return 'Sample notes content';
        return 'Sample text value';

      case 'email':
        return 'john.doe@example.com';

      case 'phone':
        return '+1 (555) 123-4567';

      case 'picklist':
        if (fieldName.includes('status')) return 'Active';
        if (fieldName.includes('type')) return 'Standard';
        if (fieldName.includes('source')) return 'Website';
        if (fieldName.includes('industry')) return 'Technology';
        if (fieldName.includes('rating')) return 'Hot';
        if (fieldName.includes('priority')) return 'Medium';
        if (fieldName.includes('stage')) return 'Prospecting';
        if (fieldName.includes('currency')) return 'USD';
        return 'Option 1';

      case 'number':
        if (fieldName.includes('revenue') || fieldName.includes('amount') || fieldName.includes('price')) return '$1,000';
        if (fieldName.includes('employees')) return '50';
        if (fieldName.includes('probability')) return '75%';
        if (fieldName.includes('quantity')) return '10';
        if (fieldName.includes('tax_rate')) return '8.5%';
        return '100';

      case 'currency':
        if (fieldName.includes('revenue') || fieldName.includes('amount') || fieldName.includes('price')) return '$1,000';
        if (fieldName.includes('subtotal')) return '$900';
        if (fieldName.includes('tax')) return '$100';
        return '$500';

      case 'date':
        return '2024-01-15';

      case 'datetime':
        return '2024-01-15 10:30:00';

      case 'url':
        return 'https://www.example.com';

      default:
        return 'Sample value';
    }
  };

  const formatEmailPreview = (content) => {
    if (!content) return '';

    // Convert plain text to proper email formatting
    let formattedContent = content;

    // Replace line breaks with <br> tags for proper display
    formattedContent = formattedContent.replace(/\n/g, '<br>');

    // Add professional email styling
    formattedContent = `
      <div style="
        font-family: Arial, sans-serif; 
        line-height: 1.6; 
        color: #333;
        max-width: 600px;
        margin: 0 auto;
        padding: 20px;
        background-color: #ffffff;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      ">
        ${formattedContent}
      </div>
    `;

    return formattedContent;
  };

  const getAvailableOperators = async (fieldType) => {
    try {
      const response = await axiosInstance.get(`/operators/${fieldType}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching operators:', error);
      // Return empty array if API fails - no fallback to hardcoded operators
      return [];
    }
  };

  const addConditionGroup = () => {
    const newGroup = {
      id: `group_${conditionGroups.length + 1}`,
      name: `Group ${conditionGroups.length + 1}`,
      logic: 'AND',
      logicMode: 'simple', // 'simple' or 'advanced'
      customLogic: '',
      conditions: []
    };
    setConditionGroups(prev => [...prev, newGroup]);
  };

  const removeConditionGroup = (groupId) => {
    setConditionGroups(prev => {
      const filtered = prev.filter(group => group.id !== groupId);
      // Renumber the remaining groups
      return filtered.map((group, index) => ({
        ...group,
        id: `group_${index + 1}`,
        name: `Group ${index + 1}`
      }));
    });
  };

  const updateGroupLogic = (groupId, logic) => {
    setConditionGroups(prev =>
      prev.map(group =>
        group.id === groupId ? { ...group, logic } : group
      )
    );
  };

  const updateGroupLogicMode = (groupId, logicMode) => {
    setConditionGroups(prev =>
      prev.map(group =>
        group.id === groupId ? {
          ...group,
          logicMode,
          customLogic: logicMode === 'advanced' ? generateDefaultLogic(group.conditions.length) : ''
        } : group
      )
    );
  };

  const updateGroupCustomLogic = (groupId, customLogic) => {
    setConditionGroups(prev =>
      prev.map(group =>
        group.id === groupId ? { ...group, customLogic } : group
      )
    );
  };

  const generateDefaultLogic = (conditionCount) => {
    if (conditionCount <= 1) return '';
    const numbers = Array.from({ length: conditionCount }, (_, i) => i + 1);
    return numbers.join(' AND ');
  };

  const validateCustomLogic = (logic, conditionCount) => {
    if (!logic.trim()) return { valid: true }; // Allow empty logic

    // Check if all condition numbers are valid
    const numbers = logic.match(/\d+/g) || [];
    const uniqueNumbers = [...new Set(numbers.map(n => parseInt(n)))];

    for (let num of uniqueNumbers) {
      if (num < 1 || num > conditionCount) {
        return { valid: false, error: `Invalid condition number: ${num}. Must be between 1 and ${conditionCount}` };
      }
    }

    // Check if logic contains valid operators and structure
    const validLogic = logic.replace(/\d+/g, 'X').replace(/\s+/g, ' ').trim();

    // Updated pattern to handle expressions starting with parentheses or numbers
    const logicPattern = /^(\(.*\)|X)(\s+(AND|OR)\s+(\(.*\)|X))*$/i;

    // Also check for balanced parentheses
    const openParens = (logic.match(/\(/g) || []).length;
    const closeParens = (logic.match(/\)/g) || []).length;

    if (openParens !== closeParens) {
      return { valid: false, error: 'Unbalanced parentheses' };
    }

    // Check for valid tokens (numbers, AND, OR, parentheses, spaces)
    const validTokens = /^[\d\s\(\)]+$|^[\d\s\(\)ANDOR]+$/i;
    const cleanLogic = logic.replace(/AND|OR/gi, '').replace(/\s+/g, '');

    if (!validTokens.test(logic)) {
      return { valid: false, error: 'Invalid characters. Use only numbers, AND, OR, and parentheses' };
    }

    // Basic structure validation - ensure we have alternating conditions and operators
    const tokens = logic.split(/\s+/).filter(token => token.length > 0);

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];

      // Check for empty parentheses
      if (token.includes('()')) {
        return { valid: false, error: 'Empty parentheses not allowed' };
      }

      // Consecutive operators check
      if (i > 0 && (token === 'AND' || token === 'OR')) {
        const prevToken = tokens[i - 1];
        if (prevToken === 'AND' || prevToken === 'OR') {
          return { valid: false, error: 'Consecutive operators not allowed' };
        }
      }
    }

    return { valid: true };
  };

  const previewGroupLogic = (group) => {
    if (group.conditions.length === 0) return 'No conditions';
    if (group.conditions.length === 1) return 'Single condition';

    if (group.logicMode === 'simple') {
      return `All conditions with ${group.logic}`;
    } else {
      if (!group.customLogic) return 'No custom logic defined';

      const validation = validateCustomLogic(group.customLogic, group.conditions.length);
      if (!validation.valid) {
        return `Invalid: ${validation.error}`;
      }

      return group.customLogic;
    }
  };

  const addConditionToGroup = (groupId) => {
    const group = conditionGroups.find(g => g.id === groupId);
    const conditionIndex = group ? group.conditions.length + 1 : 1;

    const newCondition = {
      id: `condition_${conditionIndex}`,
      field: '',
      operator: '',
      value: ''
    };

    setConditionGroups(prev =>
      prev.map(group => {
        if (group.id === groupId) {
          const newConditions = [...group.conditions, newCondition];
          return {
            ...group,
            conditions: newConditions,
            // Auto-update custom logic in advanced mode
            customLogic: group.logicMode === 'advanced' ?
              generateDefaultLogic(newConditions.length) :
              group.customLogic
          };
        }
        return group;
      })
    );
  };

  const updateConditionInGroup = (groupId, conditionId, field, value) => {
    setConditionGroups(prev =>
      prev.map(group =>
        group.id === groupId
          ? {
            ...group,
            conditions: group.conditions.map(condition =>
              condition.id === conditionId
                ? { ...condition, [field]: value }
                : condition
            )
          }
          : group
      )
    );
  };

  const removeConditionFromGroup = (groupId, conditionId) => {
    setConditionGroups(prev =>
      prev.map(group => {
        if (group.id === groupId) {
          const newConditions = group.conditions.filter(condition => condition.id !== conditionId);
          // Renumber the remaining conditions
          const renumberedConditions = newConditions.map((condition, index) => ({
            ...condition,
            id: `condition_${index + 1}`
          }));

          return {
            ...group,
            conditions: renumberedConditions,
            // Auto-update custom logic in advanced mode
            customLogic: group.logicMode === 'advanced' ?
              generateDefaultLogic(renumberedConditions.length) :
              group.customLogic
          };
        }
        return group;
      })
    );
  };

  const generateLogicPreview = () => {
    if (conditionGroups.length === 0) return 'No conditions set';

    const groupPreviews = conditionGroups.map((group, groupIndex) => {
      if (group.conditions.length === 0) return '';

      if (group.conditions.length === 1) {
        const condition = group.conditions[0];
        const field = getAvailableFields().find(f => f.id === condition.field);
        return `${field?.name || 'Field'} ${condition.operator || 'Operator'} ${condition.value || 'Value'}`;
      }

      // Handle simple logic mode
      if (group.logicMode === 'simple') {
        const conditionTexts = group.conditions.map((condition, condIndex) => {
          const field = getAvailableFields().find(f => f.id === condition.field);
          return `${field?.name || 'Field'} ${condition.operator || 'Operator'} ${condition.value || 'Value'}`;
        });

        return `(${conditionTexts.join(` ${group.logic} `)})`;
      }

      // Handle advanced logic mode
      if (group.logicMode === 'advanced' && group.customLogic) {
        const validation = validateCustomLogic(group.customLogic, group.conditions.length);
        if (!validation.valid) {
          return `Group ${groupIndex + 1}: Invalid logic`;
        }

        let logicPreview = group.customLogic;

        // Replace condition numbers with actual condition descriptions
        group.conditions.forEach((condition, condIndex) => {
          const field = getAvailableFields().find(f => f.id === condition.field);
          const conditionText = `${field?.name || 'Field'} ${condition.operator || 'Operator'} ${condition.value || 'Value'}`;

          // Replace the condition number with the actual condition
          const regex = new RegExp(`\\b${condIndex + 1}\\b`, 'g');
          logicPreview = logicPreview.replace(regex, conditionText);
        });

        return `(${logicPreview})`;
      }

      return `Group ${groupIndex + 1}: No logic defined`;
    });

    const validGroups = groupPreviews.filter(preview => preview !== '');

    if (validGroups.length === 0) return 'No valid conditions';
    if (validGroups.length === 1) return validGroups[0];

    return validGroups.join(` ${groupLogic} `);
  };

  const renderTriggerStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Configure when this automation should trigger. Choose the object and event that will start the automation.
          {wizardData.object === 'invoice' || wizardData.object === 'order' ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                Note: Invoices and orders do not support deletion, so the "On Delete" trigger is not available.
              </Typography>
            </Box>
          ) : null}
        </Alert>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Automation Name *"
          value={wizardData.name}
          onChange={(e) => updateWizardData('name', e.target.value)}
          error={showValidationErrors && !!errors.name}
          helperText={showValidationErrors && errors.name}
          placeholder="Enter a descriptive name for this automation"
          size="small"
          sx={{
            '& .MuiInputBase-root': {
              height: '40px'
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth error={showValidationErrors && !!errors.object} size="small">
          <InputLabel>Object *</InputLabel>
          <Select
            value={wizardData.object}
            onChange={(e) => updateWizardData('object', e.target.value)}
            label="Object *"
            sx={{
              height: '40px',
              '& .MuiSelect-select': {
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 14px'
              }
            }}
          >
            {availableObjects.map(obj => (
              <MenuItem key={obj.id || obj} value={obj.id || obj}>
                {typeof obj === 'string' ? obj.charAt(0).toUpperCase() + obj.slice(1) : (obj.name || obj.id || 'Unknown')}
              </MenuItem>
            ))}
          </Select>
          {showValidationErrors && errors.object && <Typography variant="caption" color="error">{errors.object}</Typography>}
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth error={showValidationErrors && !!errors.trigger} size="small">
          <InputLabel>Trigger Event *</InputLabel>
          <Select
            value={wizardData.trigger}
            onChange={(e) => updateWizardData('trigger', e.target.value)}
            label="Trigger Event *"
            disabled={!wizardData.object}
            sx={{
              height: '40px',
              '& .MuiSelect-select': {
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 14px'
              }
            }}
          >
            {getAvailableTriggers(wizardData.object).map(trigger => (
              <MenuItem key={trigger.id} value={trigger.id}>
                {trigger.name}
              </MenuItem>
            ))}
          </Select>
          {showValidationErrors && errors.trigger && <Typography variant="caption" color="error">{errors.trigger}</Typography>}
          {!wizardData.object && <Typography variant="caption" color="text.secondary">Please select an object first</Typography>}
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={wizardData.isActive}
              onChange={(e) => updateWizardData('isActive', e.target.checked)}
            />
          }
          label="Activate this automation immediately"
        />
      </Grid>
    </Grid>
  );

  const renderConditionsStep = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Add conditions to control when this automation runs. Leave empty to run on every trigger event.
      </Alert>

      {conditionGroups.length > 0 && (
        <Card sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mr: 2, fontWeight: 'bold' }}>
              Group Logic:
            </Typography>
            <ButtonGroup size="small">
              <MDButton
                variant={groupLogic === 'AND' ? 'gradient' : 'outlined'}
                color={groupLogic === 'AND' ? 'success' : 'primary'}
                onClick={() => setGroupLogic('AND')}
                sx={{
                  minWidth: '60px',
                  fontWeight: 'bold',
                  color: groupLogic === 'AND' ? '#ffffff' : '#1565C8',
                  borderColor: '#1565C8',
                  '&:hover': {
                    backgroundColor: groupLogic === 'AND' ? 'success.main' : 'rgba(25, 118, 210, 0.04)',
                    color: groupLogic === 'AND' ? '#ffffff' : '#1565C8'
                  }
                }}
              >
                AND
              </MDButton>
              <MDButton
                variant={groupLogic === 'OR' ? 'gradient' : 'outlined'}
                color={groupLogic === 'OR' ? 'warning' : 'primary'}
                onClick={() => setGroupLogic('OR')}
                sx={{
                  minWidth: '60px',
                  fontWeight: 'bold',
                  color: groupLogic === 'OR' ? '#ffffff' : '#1565C8',
                  borderColor: '#1565C8',
                  '&:hover': {
                    backgroundColor: groupLogic === 'OR' ? 'warning.main' : 'rgba(25, 118, 210, 0.04)',
                    color: groupLogic === 'OR' ? '#ffffff' : '#1565C8'
                  }
                }}
              >
                OR
              </MDButton>
            </ButtonGroup>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {groupLogic === 'AND' ?
              'All groups must be true for the automation to run' :
              'Any group can be true for the automation to run'
            }
          </Typography>
        </Card>
      )}

      {conditionGroups.map((group, groupIndex) => (
        <Card key={group.id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={1}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#1565C8',
                color: '#ffffff',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                fontWeight: 'bold',
                fontSize: '0.875rem'
              }}>
                {groupIndex + 1}
              </Box>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Group Name"
                value={group.name}
                onChange={(e) => {
                  const newGroups = [...conditionGroups];
                  newGroups[groupIndex] = { ...newGroups[groupIndex], name: e.target.value };
                  setConditionGroups(newGroups);
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    height: '40px'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Logic Mode</InputLabel>
                <Select
                  value={group.logicMode}
                  onChange={(e) => updateGroupLogicMode(group.id, e.target.value)}
                  label="Logic Mode"
                  sx={{
                    height: '40px',
                    '& .MuiSelect-select': {
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 14px'
                    }
                  }}
                >
                  <MenuItem value="simple">Simple</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={5}>
              {group.logicMode === 'simple' ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Group Logic</InputLabel>
                  <Select
                    value={group.logic}
                    onChange={(e) => updateGroupLogic(group.id, e.target.value)}
                    label="Group Logic"
                    sx={{
                      height: '40px',
                      '& .MuiSelect-select': {
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 14px'
                      }
                    }}
                  >
                    <MenuItem value="AND">AND</MenuItem>
                    <MenuItem value="OR">OR</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="Custom Logic (e.g., (1 AND 2) OR 3)"
                  value={group.customLogic}
                  onChange={(e) => updateGroupCustomLogic(group.id, e.target.value)}
                  placeholder="Enter custom logic using condition numbers, AND, OR, and parentheses"
                  error={group.conditions.length > 1 && !validateCustomLogic(group.customLogic, group.conditions.length).valid}
                  helperText={
                    group.conditions.length > 1 && !validateCustomLogic(group.customLogic, group.conditions.length).valid
                      ? validateCustomLogic(group.customLogic, group.conditions.length).error
                      : `Use numbers 1-${group.conditions.length} with AND, OR, and parentheses`
                  }
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '40px'
                    }
                  }}
                />
              )}
            </Grid>

            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Add Condition to Group">
                  <IconButton
                    color="info"
                    onClick={() => addConditionToGroup(group.id)}
                    sx={{
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.2)'
                      }
                    }}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove Group">
                  <IconButton
                    color="error"
                    onClick={() => removeConditionGroup(group.id)}
                    sx={{
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(244, 67, 54, 0.2)'
                      }
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>

          {group.conditions.length > 1 && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 2,
              mb: 1,
              p: 1,
              backgroundColor: '#f5f5f5',
              borderRadius: 1
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                <strong>Logic Preview:</strong> {previewGroupLogic(group)}
              </Typography>
            </Box>
          )}

          {group.conditions.map((condition, condIndex) => (
            <Card key={condition.id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={1}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#ff9800',
                    color: 'white',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    fontWeight: 'bold',
                    fontSize: '0.75rem'
                  }}>
                    {condIndex + 1}
                  </Box>
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Field</InputLabel>
                    <Select
                      value={condition.field}
                      onChange={(e) => updateConditionInGroup(group.id, condition.id, 'field', e.target.value)}
                      label="Field"
                      sx={{
                        height: '40px',
                        '& .MuiSelect-select': {
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0 14px'
                        }
                      }}
                    >
                      {getAvailableFieldsForConditions().map(field => (
                        <MenuItem key={field.id} value={field.id}>
                          {field.label || prettify(field.id)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <OperatorSelect
                    condition={condition}
                    group={group}
                    updateConditionInGroup={updateConditionInGroup}
                    getAvailableFields={getAvailableFields}
                  />
                </Grid>

                <Grid item xs={12} md={4}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    '& .MuiFormControl-root': {
                      width: '100%',
                    },
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      boxSizing: 'border-box',
                      alignItems: 'center',
                      fontSize: '14px', // value text font size slightly smaller
                    },
                    '& input': {
                      fontSize: '14px', //  value font size inside the input
                    },
                    '& .MuiInputLabel-root': {
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'white',
                      padding: '0 6px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      pointerEvents: 'none',
                      left: '12px', // Centered label positioning
                    },
                    '& .MuiInputLabel-shrink': {
                      top: '-8px',
                      transform: 'translateY(0)',
                      fontSize: '12px', //  Smaller label when floating
                      left: '8px',      //  Margin from left when label floats
                    }
                  }}
                >
                  <Box sx={{ width: '100%' }}> {/* ✅ wrap to enforce full width */}
                    <ValueField
                      condition={condition}
                      field={getAvailableFields().find(f => f.id === condition.field)}
                      group={group}
                      updateConditionInGroup={updateConditionInGroup}
                      getPicklistOptions={getPicklistOptions}
                      referenceMap={referenceMap}
                      referenceDataCache={referenceDataCache}
                      referenceDataLoading={referenceDataLoading}
                      fetchReferenceData={fetchReferenceData}
                      prettify={prettify}
                      isCreatingRecord={isCreatingRecord}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={1}>
                  <Tooltip title="Remove Condition">
                    <IconButton
                      color="error"
                      onClick={() => removeConditionFromGroup(group.id, condition.id)}
                      sx={{
                        backgroundColor: 'rgba(244, 67, 54, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.2)'
                        }
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Card>
          ))}

          {groupIndex < conditionGroups.length - 1 && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 2,
              mb: 1
            }}>
              <Chip
                label={groupLogic}
                color={groupLogic === 'AND' ? 'success' : 'warning'}
                size="small"
                sx={{
                  fontWeight: 'bold',
                  color: '#ffffff'
                }}
              />
            </Box>
          )}
        </Card>
      ))}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <MDButton
          variant="gradient"
          color="info"
          startIcon={<Add />}
          onClick={addConditionGroup}
          disabled={!wizardData.object}
          sx={{
            fontWeight: 'bold',
            color: '#ffffff',
            '&:hover': {
              color: '#ffffff'
            }
          }}
        >
          Add Group
        </MDButton>

        {conditionGroups.length > 0 && (
          <MDButton
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => {
              setConditionGroups([]);
            }}
            sx={{
              borderColor: '#f44336',
              color: '#f44336',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.04)',
                borderColor: '#f44336'
              }
            }}
          >
            Clear All Groups
          </MDButton>
        )}
      </Box>

      {conditionGroups.length > 0 && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Full Logic Preview:</strong> {generateLogicPreview()}
          </Typography>
        </Alert>
      )}

      {conditionGroups.some(group => group.logicMode === 'advanced') && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Advanced Logic Examples:</strong>
          </Typography>
          <Typography variant="caption" component="div">
            • Simple AND: <code>1 AND 2 AND 3</code>
          </Typography>
          <Typography variant="caption" component="div">
            • Simple OR: <code>1 OR 2 OR 3</code>
          </Typography>
          <Typography variant="caption" component="div">
            • Mixed with parentheses: <code>(1 AND 2) OR 3</code>
          </Typography>
          <Typography variant="caption" component="div">
            • Complex: <code>((1 AND 2) OR 3) AND (4 OR 5)</code>
          </Typography>
          <Typography variant="caption" component="div" sx={{ mt: 1, fontStyle: 'italic' }}>
            Use condition numbers (1, 2, 3...) with AND, OR, and parentheses to create complex logic.
          </Typography>
        </Alert>
      )}
    </Box>
  );

  const renderActionsStep = () => {
    // console.log('📋 renderActionsStep called - wizardData.actions.length:', wizardData.actions.length);
    return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        Define what happens when this automation runs. You can add multiple actions that will execute in order.
      </Alert>

      {wizardData.actions.length === 0 && (
        <Card sx={{
          p: 4,
          textAlign: 'center',
          border: '2px dashed #e0e0e0',
          backgroundColor: '#fafafa'
        }}>
          <Box sx={{ mb: 2 }}>
            <AccountTree sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Actions Configured
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add your first action to define what happens when this automation runs.
          </Typography>
        </Card>
      )}

      {wizardData.actions.map((action, index) => {
        // console.log('🎯 Rendering action:', index, 'type:', action.type, 'parameters:', action.parameters);
        return (
        <Card
          key={index}
          sx={{
            mb: 3,
            p: 3,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            position: 'relative',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#1565C8',
              color: '#ffffff',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              fontWeight: 'bold',
              fontSize: '0.875rem',
              mr: 2
            }}>
              {index + 1}
            </Box>
            <Typography variant="h6" fontWeight="bold" color="text.primary">
              Action {index + 1}
            </Typography>
            <Box sx={{ ml: 'auto' }}>
              <Tooltip title="Remove Action">
                <IconButton
                  color="error"
                  onClick={() => removeAction(index)}
                  sx={{
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.2)'
                    }
                  }}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" error={showValidationErrors && !!errors[`action_${index}_type`]}>
                <InputLabel>Action Type *</InputLabel>
                <Select
                  value={action.type}
                  onChange={(e) => updateAction(index, 'type', e.target.value)}
                  label="Action Type *"
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 14px',
                      minHeight: '40px'
                    },
                    '& .MuiOutlinedInput-root': {
                      minHeight: '40px'
                    }
                  }}
                  renderValue={(selected) => {
                    if (!selected) return <em>Select an action type</em>;
                    const selectedAction = ACTION_TYPES.find(at => at.id === selected);
                    return selectedAction ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          {selectedAction.icon === 'task' && <PlayArrow sx={{ fontSize: 16 }} />}
                          {selectedAction.icon === 'email' && <Email sx={{ fontSize: 16 }} />}
                          {selectedAction.icon === 'edit' && <Edit sx={{ fontSize: 16 }} />}
                          {selectedAction.icon === 'add' && <Add sx={{ fontSize: 16 }} />}
                          {selectedAction.icon === 'update' && <Update sx={{ fontSize: 16 }} />}
                          {selectedAction.icon === 'link' && <Webhook sx={{ fontSize: 16 }} />}
                        </Box>
                        <Typography variant="body2" fontWeight="medium">
                          {selectedAction.name}
                        </Typography>
                      </Box>
                    ) : selected;
                  }}
                >
                  <MenuItem value="">
                    <em>Select an action type</em>
                  </MenuItem>
                  {ACTION_TYPES.filter(actionType => {
                    // Hide "Update Field" action when trigger is "on delete"
                    if (wizardData.trigger === 'on delete' && actionType.id === 'Update Field') {
                      return false;
                    }
                    // Show "Update Related Record" only for "on update" and "on delete" triggers
                    if (actionType.id === 'Update Related Record') {
                      return wizardData.trigger === 'on update' || wizardData.trigger === 'on delete';
                    }
                    return true;
                  }).map(actionType => (
                    <MenuItem key={actionType.id} value={actionType.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          {actionType.icon === 'task' && <PlayArrow sx={{ fontSize: 16 }} />}
                          {actionType.icon === 'email' && <Email sx={{ fontSize: 16 }} />}
                          {actionType.icon === 'edit' && <Edit sx={{ fontSize: 16 }} />}
                          {actionType.icon === 'add' && <Add sx={{ fontSize: 16 }} />}
                          {actionType.icon === 'update' && <Update sx={{ fontSize: 16 }} />}
                          {actionType.icon === 'link' && <Webhook sx={{ fontSize: 16 }} />}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {actionType.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {actionType.description}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {showValidationErrors && errors[`action_${index}_type`] && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors[`action_${index}_type`]}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={8}>
              {action.type === 'Create Task' && (
                <Card sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Task Configuration
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Task Type</InputLabel>
                        <Select
                          value={action.parameters?.type || 'Task'}
                          onChange={e => updateAction(index, 'parameters', { ...action.parameters, type: e.target.value })}
                          label="Task Type"
                          sx={{
                            '& .MuiSelect-select': {
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px 14px',
                              minHeight: '40px'
                            },
                            '& .MuiOutlinedInput-root': {
                              minHeight: '40px'
                            }
                          }}
                        >
                          <MenuItem value="Task">Task</MenuItem>
                          <MenuItem value="Email">Email</MenuItem>
                          <MenuItem value="Call">Call</MenuItem>
                          <MenuItem value="Meeting">Meeting</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <UserAutocomplete
                        value={action.parameters?.assignedTo || ''}
                        onChange={value => updateAction(index, 'parameters', { ...action.parameters, assignedTo: value })}
                        label="Owner"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        size="small"
                        label="Title"
                        value={action.parameters?.subject || ''}
                        onChange={e => updateAction(index, 'parameters', { ...action.parameters, subject: e.target.value })}
                        error={!action.parameters?.subject}
                        helperText={!action.parameters?.subject ? 'Title is required' : ''}
                        sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Description"
                        value={action.parameters?.notes || ''}
                        onChange={e => updateAction(index, 'parameters', { ...action.parameters, notes: e.target.value })}
                        multiline
                        rows={3}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Due Date"
                        size="small"
                        type={action.parameters?.type === 'Meeting' ? 'date' : 'datetime-local'}
                        value={action.parameters?.due_date || ''}
                        onChange={e => updateAction(index, 'parameters', { ...action.parameters, due_date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: new Date().toISOString().split('T')[0] }}
                      />
                    </Grid>
                    {action.parameters?.type === 'Meeting' && (
                      <>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Start Time"
                            size="small"
                            type="time"
                            value={action.parameters?.starttime || ''}
                            onChange={e => updateAction(index, 'parameters', { ...action.parameters, starttime: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="End Time"
                            size="small"
                            type="time"
                            value={action.parameters?.endtime || ''}
                            onChange={e => updateAction(index, 'parameters', { ...action.parameters, endtime: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Related To</InputLabel>
                        <Select
                          value={action.parameters?.related_type || ''}
                          onChange={e => updateAction(index, 'parameters', { ...action.parameters, related_type: e.target.value, related_id: '' })}
                          label="Related To *"
                          error={!action.parameters?.related_type}
                          sx={{
                            '& .MuiSelect-select': {
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px 14px',
                              minHeight: '40px'
                            },
                            '& .MuiOutlinedInput-root': {
                              minHeight: '40px'
                            }
                          }}
                        >
                          <MenuItem value="account">Account</MenuItem>
                          <MenuItem value="contact">Contact</MenuItem>
                          <MenuItem value="lead">Lead</MenuItem>
                          <MenuItem value="deal">Deal</MenuItem>
                          <MenuItem value="case">Case</MenuItem>
                          <MenuItem value="order">Order</MenuItem>
                          <MenuItem value="quote">Quotation</MenuItem>
                        </Select>
                        {!action.parameters?.related_type && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                            Related To is required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    {action.parameters?.related_type && (
                      <Grid item xs={12} md={6}>
                        {/* You may want to use a custom Autocomplete for each entity type, or a generic one with dynamic options */}
                        <ReferenceAutocomplete
                          field={{ id: action.parameters.related_type + '_id', label: 'Related Entity *' }}
                          value={action.parameters?.related_id || ''}
                          onChange={val => updateAction(index, 'parameters', { ...action.parameters, related_id: val })}
                          actionIndex={index}
                          onOpenCreateModal={openCreateModal}
                          referenceMap={referenceMap}
                          referenceDataCache={referenceDataCache}
                          referenceDataLoading={referenceDataLoading}
                          fetchReferenceData={fetchReferenceData}
                          prettify={prettify}
                          isCreatingRecord={isCreatingRecord}
                          required
                          error={!action.parameters?.related_id}
                          helperText={!action.parameters?.related_id ? 'Related Entity is required' : ''}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={action.parameters?.status || 'Scheduled'}
                          onChange={e => updateAction(index, 'parameters', { ...action.parameters, status: e.target.value })}
                          label="Status"
                          sx={{
                            '& .MuiSelect-select': {
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px 14px',
                              minHeight: '40px'
                            },
                            '& .MuiOutlinedInput-root': {
                              minHeight: '40px'
                            }
                          }}
                        >
                          <MenuItem value="Scheduled">Scheduled</MenuItem>
                          <MenuItem value="Completed">Completed</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Priority</InputLabel>
                        <Select
                          value={action.parameters?.priority || 'Medium'}
                          onChange={e => updateAction(index, 'parameters', { ...action.parameters, priority: e.target.value })}
                          label="Priority"
                          sx={{
                            '& .MuiSelect-select': {
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px 14px',
                              minHeight: '40px'
                            },
                            '& .MuiOutlinedInput-root': {
                              minHeight: '40px'
                            }
                          }}
                        >
                          <MenuItem value="High">
                            <Chip label="High" color="error" size="small" sx={{ mr: 1 }} />
                            High Priority
                          </MenuItem>
                          <MenuItem value="Medium">
                            <Chip label="Medium" color="warning" size="small" sx={{ mr: 1 }} />
                            Medium Priority
                          </MenuItem>
                          <MenuItem value="Low">
                            <Chip label="Low" color="success" size="small" sx={{ mr: 1 }} />
                            Low Priority
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Card>
              )}

              {action.type === 'Send Email' && (
                <Card sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Email Configuration
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Email Template</InputLabel>
                          <Select
                            value={action.parameters?.template || ''}
                            onChange={(e) => handleTemplateSelection(e.target.value, index)}
                            label="Email Template"
                            error={!!errors[`action_${index}_template`]}
                            sx={{
                              '& .MuiSelect-select': {
                                display: 'flex',
                                alignItems: 'center',
                                padding: '8px 14px',
                                minHeight: '40px'
                              },
                              '& .MuiOutlinedInput-root': {
                                minHeight: '40px'
                              }
                            }}
                          >
                            <MenuItem value="">
                              <em>Select template</em>
                            </MenuItem>
                            {isLoadingEmailTemplates ? (
                              <MenuItem disabled>
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                Loading templates...
                              </MenuItem>
                            ) : emailTemplates.length === 0 ? (
                              <MenuItem disabled>
                                No email templates available
                              </MenuItem>
                            ) : (
                              emailTemplates.map(template => (
                                <MenuItem key={template.id} value={template.id}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography variant="body2" fontWeight="medium">
                                      {template.name} ({template.format?.toUpperCase()})
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="To Email"
                        value={action.parameters?.toEmail || ''}
                        onChange={(e) => updateAction(index, 'parameters', { ...action.parameters, toEmail: e.target.value })}
                        placeholder="recipient@example.com"
                        type="email"
                        required
                        error={showValidationErrors && !!errors[`action_${index}_toEmail`]}
                        helperText={showValidationErrors && errors[`action_${index}_toEmail`] ? 'To Email is required' : ''}
                        sx={{
                          '& .MuiInputBase-root': {
                            height: '40px'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="CC Email (Optional)"
                        value={action.parameters?.ccEmail || ''}
                        onChange={(e) => updateAction(index, 'parameters', { ...action.parameters, ccEmail: e.target.value })}
                        placeholder="cc@example.com"
                        type="email"
                        sx={{
                          '& .MuiInputBase-root': {
                            height: '40px'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Subject Override (Optional)"
                        value={action.parameters?.subjectOverride || ''}
                        onChange={(e) => updateAction(index, 'parameters', { ...action.parameters, subjectOverride: e.target.value })}
                        placeholder="Custom email subject"
                        sx={{
                          '& .MuiInputBase-root': {
                            height: '40px'
                          }
                        }}
                      />
                    </Grid>

                    {/* Email Content Editor */}
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Email Content
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                          Write your email content below. Use field codes like {'{{field_name}}'}, {'{{company}}'} to include dynamic data.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                          {/* Available Fields Dropdown */}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                              {wizardData.object
                                ? `Available ${wizardData.object.charAt(0).toUpperCase() + wizardData.object.slice(1)} Fields:`
                                : 'Select an object first'}
                            </Typography>
                            <FormControl size="small" fullWidth>
                              <Select
                                value=""
                                onChange={(e) => handleFieldSelection(e.target.value, index)}
                                displayEmpty
                                sx={{
                                  backgroundColor: 'transparent',
                                  '& .MuiSelect-select': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '8px 14px',
                                    minHeight: '40px'
                                  },
                                  '& .MuiOutlinedInput-root': { minHeight: '40px' }
                                }}
                              >
                                <MenuItem value="" disabled>
                                  Select a field to copy
                                </MenuItem>
                                {getAvailableFieldsForEmail().map((field) => (
                                  <MenuItem key={field.id} value={field.code}>
                                    <Typography variant="body2">{field.label}</Typography>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>

                          {/* Copied Field Code Display (side-by-side) */}
                          {copiedFieldCode && (
                            <Box
                              sx={{
                                flex: 1,
                                backgroundColor: 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}
                            >
                              <TextField
                                value={copiedFieldCode}
                                size="small"
                                fullWidth
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    fontFamily: FONT_MONO,
                                    backgroundColor: 'transparent',
                                    minHeight: '40px'
                                  }
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    minHeight: '40px'
                                  }
                                }}
                                variant="outlined"
                              />
                              <IconButton
                                size="small"
                                onClick={() => copyFieldCode(copiedFieldCode)}
                                sx={{ color: 'info.main' }}
                              >
                                <ContentCopy />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => setCopiedFieldCode('')}
                                sx={{ color: '#666' }}
                              >
                                <Close fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>


                        {/* Content Editor */}
                        <Box sx={{ mb: 2 }}>
                        <TextField
                              fullWidth
                              multiline
                              rows={8}
                              label="Email Content"
                              value={action.parameters?.content || ''}
                              onChange={(e) =>
                                updateAction(index, 'parameters', {
                                  ...action.parameters,
                                  content: e.target.value,
                                })
                              }
                              placeholder="Write your email content here... Use {{field_name}} to include dynamic data."
                              helperText={
                                action.parameters?.template
                                  ? 'Template content loaded. You can modify it and add dynamic fields.'
                                  : 'Write your email content or select a template above.'
                                      }
                                      sx={{
                                        '& .MuiInputBase-root': {
                                          fontFamily: FONT_MONO,
                                        },
                                        '& .MuiInputBase-input': {
                                          p: 1,   // padding = 1
                                          m: 2,   // margin = 2
                                        },
                                      }}
                                    />

                        </Box>

                        {/* Preview Button */}
                        {action.parameters?.content && (
                          <Box sx={{ mb: 2 }}>
                            <Button
                              variant="contained"
                              color="info"
                              size="small"
                              onClick={() => {
                                setPreviewContent(action.parameters.content);
                                setOpenContentPreviewDialog(true);
                              }}
                              startIcon={<VisibilityIcon />}
                              sx={{ mr: 1 }}
                            >
                              Preview Email
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                              Preview your email with sample data
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              )}

              {action.type === 'Update Field' && (
                <Card sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Field Update Configuration
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Field to Update</InputLabel>
                        <Select
                          value={action.parameters?.field || ''}
                          onChange={(e) => updateAction(index, 'parameters', { ...action.parameters, field: e.target.value, value: '' })}
                          label="Field to Update"
                          error={!!errors[`action_${index}_field`]}
                          sx={{
                            '& .MuiSelect-select': {
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px 14px',
                              minHeight: '40px'
                            },
                            '& .MuiOutlinedInput-root': {
                              minHeight: '40px'
                            }
                          }}
                        >
                          <MenuItem value="">
                            <em>Select field</em>
                          </MenuItem>
                          {getAvailableFields().map(field => (
                            <MenuItem key={field.id} value={field.id}>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {field.label || prettify(field.id)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {field.type}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {(() => {
                        const selectedField = getAvailableFields().find(f => f.id === action.parameters?.field);
                        if (selectedField?.type === 'picklist') {
                          return (
                            <FormControl fullWidth size="small">
                              <InputLabel>New Value</InputLabel>
                              <Select
                                value={action.parameters?.value || ''}
                                onChange={(e) => updateAction(index, 'parameters', { ...action.parameters, value: e.target.value })}
                                label="New Value"
                                error={!!errors[`action_${index}_value`]}
                                sx={{
                                  '& .MuiSelect-select': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '8px 14px',
                                    minHeight: '40px'
                                  },
                                  '& .MuiOutlinedInput-root': {
                                    minHeight: '40px'
                                  }
                                }}
                              >
                                <MenuItem value="">
                                  <em>Select value</em>
                                </MenuItem>
                                {(selectedField.options || []).map(option => (
                                  <MenuItem key={option} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          );
                        } else if (selectedField?.type === 'reference' && referenceMap[selectedField.id]) {
                          return (
                            <ReferenceAutocomplete
                              field={selectedField}
                              value={action.parameters?.value || ''}
                              onChange={(value) => updateAction(index, 'parameters', { ...action.parameters, value })}
                              actionIndex={index}
                              onOpenCreateModal={openCreateModal}
                              referenceMap={referenceMap}
                              referenceDataCache={referenceDataCache}
                              referenceDataLoading={referenceDataLoading}
                              fetchReferenceData={fetchReferenceData}
                              prettify={prettify}
                              isCreatingRecord={isCreatingRecord}
                            />
                          );
                        } else if (selectedField?.type === 'date' || selectedField?.type === 'datetime' || isDateTimeField(selectedField)) {
                          return (
                            <RelativeDateInput
                              label="New Value"
                              value={action.parameters?.value || ''}
                              onChange={(val) => updateAction(index, 'parameters', { ...action.parameters, value: val })}
                            />
                          );
                        } else if (selectedField?.id === 'assigned_to') {
                          return (
                            <UserAutocomplete
                              value={action.parameters?.value || ''}
                              onChange={(value) => updateAction(index, 'parameters', { ...action.parameters, value })}
                              label="New Value"
                            />
                          );
                        } else {
                          return (
                            <TextField
                              fullWidth
                              size="small"
                              label="New Value"
                              value={action.parameters?.value || ''}
                              onChange={(e) => updateAction(index, 'parameters', { ...action.parameters, value: e.target.value })}
                              error={!!errors[`action_${index}_value`]}
                              helperText={errors[`action_${index}_value`]}
                              placeholder="Enter new value"
                              type={selectedField?.type === 'number' || selectedField?.type === 'currency' ? 'number' : 'text'}
                              sx={{
                                '& .MuiInputBase-root': {
                                  height: '40px'
                                }
                              }}
                            />
                          );
                        }
                      })()}
                    </Grid>
                  </Grid>
                </Card>
              )}

              {action.type === 'Create Record' && (
                <Card sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Record Creation Configuration
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Record Type</InputLabel>
                        <Select
                          value={action.parameters?.recordType || ''}
                          onChange={(e) => updateAction(index, 'parameters', { ...action.parameters, recordType: e.target.value })}
                          label="Record Type"
                          sx={{
                            '& .MuiSelect-select': {
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px 14px',
                              minHeight: '40px'
                            },
                            '& .MuiOutlinedInput-root': {
                              minHeight: '40px'
                            }
                          }}
                        >
                          <MenuItem value="">
                            <em>Select record type</em>
                          </MenuItem>
                          {createRecordTypeOptions.map(obj => (
                            <MenuItem key={obj} value={obj}>
                              {obj.charAt(0).toUpperCase() + obj.slice(1)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Special handling for Deal creation - Account and Contact are mandatory */}
                    {action.parameters?.recordType === 'deal' &&  (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Note:</strong> When creating a Deal, Please fill mandatory fields.
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                    {action.parameters?.recordType === 'account' && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Note:</strong> When creating an Account, Please fill mandatory fields.
                          </Typography>
                        </Alert>
                      </Grid>
                    )}

                       {action.parameters?.recordType === 'order' && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Note:</strong> When creating an Order, please fill mandatory fields. Order number will be automatically generated when automation triggers.
                          </Typography>
                        </Alert>
                      </Grid>
                    )}

                    {action.parameters?.recordType === 'contact' && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Note:</strong> When creating an Contact, Please fill mandatory fields.
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                    {action.parameters?.recordType === 'quotation' && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Note:</strong> When creating a Quotation, please fill mandatory fields. Quote name will be automatically generated when automation triggers.
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                      {action.parameters?.recordType === 'lead' && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Note:</strong> When creating an Lead, Please fill mandatory fields.
                          </Typography>
                        </Alert>
                      </Grid>
                    )}

                      {action.parameters?.recordType === 'product' && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Note:</strong> When creating an product, Please fill mandatory fields.
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                      {action.parameters?.recordType === 'invoice' && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Note:</strong> When creating an Invoice, please fill mandatory fields. Invoice number will be automatically generated when automation triggers.
                          </Typography>
                        </Alert>
                      </Grid>
                    )}

                    {/* Render all required fields for the selected record type */}
                    
                    {createRecordFields.length === 0 && action.parameters?.recordType && (
                      <Grid item xs={12}>
                        <Alert severity="info">
                          <Typography variant="body2">
                            Loading fields for {action.parameters.recordType}...
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                    


                    {createRecordFields.map((field, fieldIndex) => {
                      // console.log('🔧 Rendering field:', field.id, 'for action:', index, 'fieldIndex:', fieldIndex);

                      
                      const isMandatoryForDeal =
                        action.parameters?.recordType === 'deal' &&
                        (field.id === 'account_id' || field.id === 'contact_id');

                      const isRequiredFromBackend = field.required;
                      const isRequired = isMandatoryForDeal || isRequiredFromBackend;

                      const isChecked = isRequired || !!selectedCreateFields[field.id];
                      // For auto-generated fields, show the generated value or the parameter value
                      let fieldValue = action.parameters?.[field.id];
                      

                      
                      // If it's an auto-generated field and no value is set, show a preview of what will be generated
                      if (!fieldValue && (
                        (action.parameters?.recordType === 'quotation' && field.id === 'quote_name') ||
                        (action.parameters?.recordType === 'order' && field.id === 'order_number') ||
                        (action.parameters?.recordType === 'invoice' && field.id === 'invoice_number')
                      )) {
                        // Show a preview of the generated value
                        if (action.parameters?.recordType === 'quotation' && field.id === 'quote_name') {
                          fieldValue = 'Quote-[Auto-generated]';
                        } else if (action.parameters?.recordType === 'order' && field.id === 'order_number') {
                          fieldValue = 'ORD-[Auto-generated]';
                        } else if (action.parameters?.recordType === 'invoice' && field.id === 'invoice_number') {
                          fieldValue = 'INV-[Auto-generated]';
                        }
                      }

                      // For auto-generated fields, don't show validation errors even if fieldValue is a preview
                      const isAutoGeneratedField = (
                        (action.parameters?.recordType === 'quotation' && field.id === 'quote_name') ||
                        (action.parameters?.recordType === 'order' && field.id === 'order_number') ||
                        (action.parameters?.recordType === 'invoice' && field.id === 'invoice_number')
                      );
                      
                      const showError = showValidationErrors && isRequired && !fieldValue && !isAutoGeneratedField;
                      const handleChange = (value) => {
                        updateAction(index, 'parameters', {
                          ...action.parameters,
                          [field.id]: value
                        });
                      };

                      // Set default value for picklist fields if not already set
                      if (field.type === 'picklist' && field.default && !action.parameters?.[field.id]) {
                        updateAction(index, 'parameters', {
                          ...action.parameters,
                          [field.id]: field.default
                        });
                      }

                      return (
                        <Grid item xs={12} md={6} key={field.id}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: 1,
                            p: 2,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            backgroundColor: '#fafafa',
                            height: '100%',
                            minHeight: '120px'
                          }}>
                            {/* Checkbox and Label Section - Full Width */}
                            <Box sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%'
                            }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={isChecked}
                                    disabled={isRequired}
                                    onChange={e => {
                                      if (!isRequired) {
                                        setSelectedCreateFields(prev => ({
                                          ...prev,
                                          [field.id]: e.target.checked
                                        }));
                                        if (!e.target.checked) {
                                          updateAction(index, 'parameters', {
                                            ...action.parameters,
                                            [field.id]: undefined
                                          });
                                        }
                                      }
                                    }}
                                  />
                                }
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {field.label || prettify(field.id)}
                                    </Typography>
                                    {isRequired && (
                                      <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                                    )}
                                    {/* Show auto-generation indicator for specific fields */}
                                    {action.parameters?.recordType === 'quotation' && field.id === 'quote_name' && (
                                      <Chip
                                        label="Will generate automatically"
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        sx={{ ml: 1, height: '20px', fontSize: '0.6rem' }}
                                      />
                                    )}
                                    {action.parameters?.recordType === 'order' && field.id === 'order_number' && (
                                      <Chip
                                        label="Will generate automatically"
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        sx={{ ml: 1, height: '20px', fontSize: '0.6rem' }}
                                      />
                                    )}
                                    {action.parameters?.recordType === 'invoice' && field.id === 'invoice_number' && (
                                      <Chip
                                        label="Will generate automatically"
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        sx={{ ml: 1, height: '20px', fontSize: '0.6rem' }}
                                      />
                                    )}

                                  </Box>
                                }
                              />
                            </Box>

                            {/* Input Field Section - Full Width */}
                            {isChecked && (
                              <Box sx={{ 
                                width: '100%',
                                '& .MuiFormControl-root': {
                                  width: '100%'
                                }
                              }}>
                                {/* ---------- PICKLIST (DROPDOWN) FIELD ---------- */}
                                {field.type === 'picklist' ? (
                                  // Debug for quote field in picklist
                                  action.parameters?.recordType === 'quotation' && field.id === 'quote_name' 
                                ) : null}
                                {field.type === 'picklist' ? (
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    required={isRequired}
                                  >
                                    <InputLabel id={`label-${field.id}`} shrink>
                                      {field.label || prettify(field.id)}
                                    </InputLabel>
                                    <Select
                                      labelId={`label-${field.id}`}
                                      id={`select-${field.id}`}
                                      value={fieldValue || ''}
                                      onChange={e => handleChange(e.target.value)}
                                      label={field.label || prettify(field.id)}  
                                      displayEmpty
                                      inputProps={{
                                        'aria-label': field.label || prettify(field.id)
                                      }}
                                      sx={{
                                        height: '40px',
                                        '& .MuiSelect-select': {
                                          height: '40px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          padding: '0 14px'
                                        }
                                      }}
                                    >
                                      <MenuItem value="">
                                        <em>Select {field.label || prettify(field.id)}</em>
                                      </MenuItem>
                                      {(field.options || []).map(option => (
                                        <MenuItem key={option} value={option}>
                                          {option}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>


                                ) : field.type === 'reference' && referenceMap[field.id] ? (
                                  // ---------- REFERENCE AUTOCOMPLETE ----------
                                  // Debug for quote field in reference
                                  (() => {
                                    return (
                                      <Box>

                                        <ReferenceAutocomplete
                                          field={field}
                                          value={fieldValue || ''}
                                          onChange={(value) => {
                                            // If this is an account field and we're creating a record that has deal_id field, handle both account_id and deal_id clearing
                                            if (field.id === 'account_id' && action.parameters?.recordType && 
                                                (action.parameters?.recordType === 'deal' || action.parameters?.recordType === 'quotation')) {
                                              setSelectedAccountForDeals(value);
                                              // Update parameters with account_id and clear deal_id in one call
                                              const updatedParameters = {
                                                ...action.parameters,
                                                account_id: value,
                                                deal_id: undefined
                                              };
                                              updateAction(index, 'parameters', updatedParameters);
                                            } else {
                                              // For other fields, use the normal handleChange
                                              handleChange(value);
                                            }
                                          }}
                                          actionIndex={index}
                                          onOpenCreateModal={openCreateModal}
                                          referenceMap={referenceMap}
                                          referenceDataCache={referenceDataCache}
                                          referenceDataLoading={referenceDataLoading}
                                          fetchReferenceData={fetchReferenceData}
                                          prettify={prettify}
                                          isCreatingRecord={isCreatingRecord}
                                          selectedAccountForDeals={selectedAccountForDeals}
                                        />
                                      </Box>
                                    );
                                  })()
                                ) : (
                                  // ---------- TEXT OR DATE FIELD ----------
                                  (field.type === 'date' || field.type === 'datetime' || isDateTimeField(field)) ? (
                                    <RelativeDateInput
                                      label={field.label || prettify(field.id)}
                                      value={fieldValue || ''}
                                      onChange={handleChange}
                                    />
                                  ) : (
                                    <TextField
                                      fullWidth
                                      size="small"
                                      type="text"
                                      label={field.label || prettify(field.id)}
                                      value={fieldValue || ''}
                                      onChange={e => handleChange(e.target.value)}
                                      InputLabelProps={{ shrink: true }}
                                      // Make auto-generated fields read-only and greyed out
                                      InputProps={{
                                        readOnly: (
                                          (action.parameters?.recordType === 'quotation' && field.id === 'quote_name') ||
                                          (action.parameters?.recordType === 'order' && field.id === 'order_number') ||
                                          (action.parameters?.recordType === 'invoice' && field.id === 'invoice_number')
                                        ),
                                        endAdornment: (
                                          (action.parameters?.recordType === 'quotation' && field.id === 'quote_name') ||
                                          (action.parameters?.recordType === 'order' && field.id === 'order_number') ||
                                          (action.parameters?.recordType === 'invoice' && field.id === 'invoice_number')
                                        ) ? (
                                            <Tooltip title="This field will be generated automatically when automation triggers">
                                              <Info sx={{ fontSize: 16, color: 'info.main' }} />
                                            </Tooltip>
                                          ) : null
                                      }}
                                      placeholder={
                                        (action.parameters?.recordType === 'quotation' && field.id === 'quote_name') ? 'Will generate automatically when automation triggers' :
                                        (action.parameters?.recordType === 'order' && field.id === 'order_number') ? 'Will generate automatically when automation triggers' :
                                        (action.parameters?.recordType === 'invoice' && field.id === 'invoice_number') ? 'Will generate automatically when automation triggers' :
                                        undefined
                                      }
                                      // Debug logging
                                      onFocus={() => {
                                        if (action.parameters?.recordType === 'quotation' && field.id === 'quote_name') {
                                          //console.log('DEBUG: Quote field focused', { recordType: action.parameters?.recordType, fieldId: field.id });
                                        }
                                      }}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          height: '40px'
                                        },
                                        // Style for auto-generated fields
                                        ...((action.parameters?.recordType === 'quotation' && field.id === 'quote_name') ||
                                            (action.parameters?.recordType === 'order' && field.id === 'order_number') ||
                                            (action.parameters?.recordType === 'invoice' && field.id === 'invoice_number') ? {
                                          '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#f5f5f5',
                                            borderColor: '#bdbdbd',
                                            color: '#757575'
                                          },
                                          '& .MuiInputBase-input': {
                                            color: '#757575'
                                          }
                                        } : {})
                                      }}
                                    />
                                  )
                                )}
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Card>
              )}
              
              {action.type === 'Update Related Record' && (
                <Card sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Related Record Update Configuration
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small" error={showValidationErrors && !!errors[`action_${index}_relatedRecordType`]}>
                        <InputLabel>Related Record Type <span style={{ color: 'red' }}>*</span>
                        </InputLabel>
                        <Select
                          value={action.parameters?.relatedRecordType || ''}
                          onChange={(e) => updateAction(index, 'parameters', { ...action.parameters, relatedRecordType: e.target.value, relatedField: '', relatedValue: '' })}
                          label="Related Record Type"
                          sx={{
                            height: '40px',
                            '& .MuiSelect-select': {
                              height: '40px',
                              display: 'flex',
                              alignItems: 'center',
                              padding: '0 14px'
                            }
                          }}
                        >
                          <MenuItem value="">
                            <em>Select type</em>
                          </MenuItem>
                          {relatedObjects.map(obj => (
                            <MenuItem key={obj} value={obj}>
                              {obj.charAt(0).toUpperCase() + obj.slice(1)}
                            </MenuItem>
                          ))}
                        </Select>
                        {showValidationErrors && errors[`action_${index}_relatedRecordType`] && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                            {errors[`action_${index}_relatedRecordType`]}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small" error={showValidationErrors && !!errors[`action_${index}_relatedField`]}>
                        <InputLabel>Field to Update <span style={{ color: 'red' }}>*</span>
                        </InputLabel>
                        <Select
                          value={action.parameters?.relatedField || ''}
                          onChange={(e) => updateAction(index, 'parameters', { ...action.parameters, relatedField: e.target.value })}
                          label="Field to Update"
                          sx={{
                            height: '40px',
                            '& .MuiSelect-select': {
                              height: '40px',
                              display: 'flex',
                              alignItems: 'center',
                              padding: '0 14px'
                            }
                          }}
                        >
                          <MenuItem value="">
                            <em>Select field</em>
                          </MenuItem>
                          {relatedRecordFields.map(field => (
                            <MenuItem key={field.id} value={field.id}>{field.label || prettify(field.id)}</MenuItem>
                          ))}
                        </Select>
                        {showValidationErrors && errors[`action_${index}_relatedField`] && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                            {errors[`action_${index}_relatedField`]}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {(() => {
                        const selectedField = relatedRecordFields.find(f => f.id === action.parameters?.relatedField);
                        if (selectedField?.type === 'picklist') {
                          return (
                            <FormControl fullWidth size="small" error={showValidationErrors && !!errors[`action_${index}_relatedValue`]}>
                              <InputLabel>New Value <span style={{ color: 'red' }}>*</span>
                              </InputLabel>
                              <Select
                                value={action.parameters?.relatedValue || ''}
                                onChange={(e) => updateAction(index, 'parameters', { ...action.parameters, relatedValue: e.target.value })}
                                label="New Value *"
                                sx={{
                                  height: '40px',
                                  '& .MuiSelect-select': {
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '0 14px'
                                  }
                                }}
                              >
                                <MenuItem value=""><em>Select value</em></MenuItem>
                                {(selectedField.options || []).map(option => (
                                  <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                              </Select>
                              {showValidationErrors && errors[`action_${index}_relatedValue`] && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                  {errors[`action_${index}_relatedValue`]}
                                </Typography>
                              )}
                            </FormControl>
                          );
                        } else if (selectedField?.type === 'reference' && referenceMap[selectedField.id]) {
                          return (
                            <ReferenceAutocomplete
                              field={selectedField}
                              value={action.parameters?.relatedValue || ''}
                              onChange={val => updateAction(index, 'parameters', { ...action.parameters, relatedValue: val })}
                              actionIndex={index}
                              onOpenCreateModal={openCreateModal}
                              referenceMap={referenceMap}
                              referenceDataCache={referenceDataCache}
                              referenceDataLoading={referenceDataLoading}
                              fetchReferenceData={fetchReferenceData}
                              prettify={prettify}
                              isCreatingRecord={isCreatingRecord}
                            />
                          );
                        } else if (selectedField?.type === 'date' || selectedField?.type === 'datetime' || isDateTimeField(selectedField)) {
                          return (
                            <RelativeDateInput
                              label="New Value"
                              value={action.parameters?.relatedValue || ''}
                              onChange={(val) => updateAction(index, 'parameters', { ...action.parameters, relatedValue: val })}
                            />
                          );
                        } else {
                          return (
                            <TextField
                              fullWidth
                              size="small"
                              label="New Value"
                              value={action.parameters?.relatedValue || ''}
                              onChange={(e) => updateAction(index, 'parameters', { ...action.parameters, relatedValue: e.target.value })}
                              error={showValidationErrors && !!errors[`action_${index}_relatedValue`]}
                              helperText={showValidationErrors && errors[`action_${index}_relatedValue`] ? errors[`action_${index}_relatedValue`] : ''}
                            />
                          );
                        }
                      })()}
                    </Grid>
                  </Grid>
                </Card>
              )}

              {action.type === 'Webhook' && (
                <Card sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Webhook Configuration
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small" error={showValidationErrors && !!errors[`action_${index}_webhook`]}>
                        <InputLabel>Select Webhook *</InputLabel>
                        <Select
                          value={action.parameters?.webhookId || ''}
                          onChange={(e) => {
                            const selectedWebhook = configuredWebhooks.find(w => w.id === e.target.value);
                            updateAction(index, 'parameters', {
                              ...action.parameters,
                              webhookId: e.target.value,
                              url: selectedWebhook?.url || '',
                              method: selectedWebhook?.method || 'GET',
                              name: selectedWebhook?.name || ''
                            });
                          }}
                          label="Select Webhook *"
                          sx={{
                            '& .MuiSelect-select': {
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px 14px',
                              minHeight: '40px'
                            },
                            '& .MuiOutlinedInput-root': {
                              minHeight: '40px'
                            }
                          }}
                          renderValue={(selected) => {
                            if (!selected) return <em>Select a configured webhook</em>;
                            const selectedWebhook = configuredWebhooks.find(w => w.id === selected);
                            return selectedWebhook ? selectedWebhook.name : selected;
                          }}
                        >
                          <MenuItem value="">
                            <em>Select a configured webhook</em>
                          </MenuItem>
                          {configuredWebhooks.map(webhook => (
                            <MenuItem key={webhook.id} value={webhook.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" fontWeight="medium">
                                    {webhook.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {webhook.url} ({webhook.method})
                                  </Typography>
                                </Box>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                        {showValidationErrors && errors[`action_${index}_webhook`] && (
                          <Typography variant="caption" color="error">
                            {errors[`action_${index}_webhook`]}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Webhook URL"
                        value={action.parameters?.url || ''}
                        InputProps={{ readOnly: true }}
                        sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="HTTP Method"
                        value={action.parameters?.method || 'GET'}
                        InputProps={{ readOnly: true }}
                        sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                      />
                    </Grid>

                    {/* Field Mapping Section */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                        Data Field Mapping
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select which fields from the {wizardData.object} record to send to the webhook. For GET requests, these will be added as URL parameters. For POST requests, they will be sent in the request body. Fields with no data will be sent as "nan".
                      </Typography>

                      {/* Available Fields */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          Available Fields:
                        </Typography>
                        <Grid container spacing={1}>
                          {getAvailableFields().map(field => (
                            <Grid item xs={12} md={6} key={field.id}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={!!action.parameters?.fieldMapping?.[field.id]}
                                    onChange={(e) => {
                                      const fieldMapping = action.parameters?.fieldMapping || {};
                                      if (e.target.checked) {
                                        fieldMapping[field.id] = field.id; // Default mapping: field name = field value
                                      } else {
                                        delete fieldMapping[field.id];
                                      }
                                      updateAction(index, 'parameters', {
                                        ...action.parameters,
                                        fieldMapping
                                      });
                                    }}
                                  />
                                }
                                label={
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2">
                                        {field.label || prettify(field.id)}
                                      </Typography>
                                      {field.is_custom && (
                                        <Chip
                                          label="Custom"
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                          sx={{ height: '16px', fontSize: '0.6rem' }}
                                        />
                                      )}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                      {field.type}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>

                      {/* Custom Fields Section */}
                      {(() => {
                        const customFieldsFromObject = getAvailableFields().filter(field => field.is_custom);
                        return customFieldsFromObject.length > 0 && (
                          <Box sx={{ mb: 2, mt: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              Custom Fields Available
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              The following custom fields are available for this {wizardData.object} and can be mapped to webhook parameters:
                            </Typography>
                            <Grid container spacing={1}>
                              {customFieldsFromObject.map(field => (
                                <Grid item xs={12} md={6} key={field.id}>
                                  <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    p: 1,
                                    backgroundColor: '#ffffff',
                                    borderRadius: 1,
                                    border: '1px solid #e0e0e0'
                                  }}>
                                    <Chip
                                      label="Custom"
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ height: '16px', fontSize: '0.6rem' }}
                                    />
                                    <Typography variant="body2" fontWeight="medium">
                                      {field.label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      ({field.type})
                                    </Typography>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        );
                      })()}

                      {/* Custom Parameters */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          Custom Parameters (Optional):
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Add additional parameters that will be sent with the webhook. For GET requests, these will be added to the URL. For other methods, they will be sent in the request body.
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          {action.parameters?.customParams?.map((param, paramIndex) => (
                            <Box key={paramIndex} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                              <TextField
                                size="small"
                                label="Parameter Name"
                                value={param.key || ''}
                                onChange={(e) => {
                                  const newParams = [...(action.parameters?.customParams || [])];
                                  newParams[paramIndex] = { ...param, key: e.target.value };
                                  updateAction(index, 'parameters', { ...action.parameters, customParams: newParams });
                                }}
                                sx={{ flex: 1, '& .MuiInputBase-root': { height: '40px' } }}
                              />
                              <TextField
                                size="small"
                                label="Parameter Value"
                                value={param.value || ''}
                                onChange={(e) => {
                                  const newParams = [...(action.parameters?.customParams || [])];
                                  newParams[paramIndex] = { ...param, value: e.target.value };
                                  updateAction(index, 'parameters', { ...action.parameters, customParams: newParams });
                                }}
                                sx={{ flex: 1, '& .MuiInputBase-root': { height: '40px' } }}
                              />
                              <IconButton
                                color="error"
                                onClick={() => {
                                  const newParams = action.parameters?.customParams?.filter((_, i) => i !== paramIndex) || [];
                                  updateAction(index, 'parameters', { ...action.parameters, customParams: newParams });
                                }}
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          ))}
                          <MDButton
                            variant="gradient"
                            color="info"
                            size="small"
                            startIcon={<Add />}
                            onClick={() => {
                              const newParams = [...(action.parameters?.customParams || []), { key: '', value: '' }];
                              updateAction(index, 'parameters', { ...action.parameters, customParams: newParams });
                            }}
                            sx={{
                              fontWeight: 'bold',
                              color: '#ffffff',
                              backgroundColor: '#1565C8',
                              '&:hover': {
                                backgroundColor: '#0E4FA3',
                                color: '#ffffff'
                              }
                            }}
                          >
                            Add Custom Parameter
                          </MDButton>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <MDButton
                          variant="gradient"
                          color="success"
                          size="small"
                          onClick={async () => {
                            try {
                              // Build field mapping and custom params for test
                              const fieldMapping = action.parameters?.fieldMapping || {};
                              const customParams = {};

                              // Add custom params
                              if (action.parameters?.customParams) {
                                action.parameters.customParams.forEach(param => {
                                  if (param.key) {
                                    customParams[param.key] = param.value;
                                  }
                                });
                              }

                              const response = await axiosInstance.get('/webhook/test', {
                                params: {
                                  url: action.parameters?.url,
                                  method: action.parameters?.method || 'GET',
                                  params: {},
                                  field_mapping: JSON.stringify(fieldMapping),
                                  custom_params: JSON.stringify(customParams)
                                }
                              });
                              if (response.data.success) {
                                alert('Webhook test successful!');
                              } else {
                                alert(`Webhook test failed: ${response.data.response_text}`);
                              }
                            } catch (error) {
                              alert(`Webhook test failed: ${error.response?.data?.detail || error.message}`);
                            }
                          }}
                          disabled={!action.parameters?.url}
                          sx={{
                            fontWeight: 'bold',
                            color: '#ffffff',
                            backgroundColor: '#4caf50',
                            '&:hover': {
                              backgroundColor: '#45a049',
                              color: '#ffffff'
                            },
                            '&:disabled': {
                              backgroundColor: '#cccccc',
                              color: '#666666'
                            }
                          }}
                        >
                          Test Webhook
                        </MDButton>
                        <Typography variant="caption" color="text.secondary">
                          Test the webhook configuration before saving
                        </Typography>
                      </Box>
                    </Grid>
                    {configuredWebhooks.length === 0 && (
                      <Grid item xs={12}>
                        <Alert severity="warning">
                          <Typography variant="body2">
                            No webhooks configured. Please configure webhooks in{' '}
                            <strong>Settings → Integrations → Webhook Integrations</strong> first.
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Card>
              )}

              {/* Time Delay Section */}
              {action.type && (
                <Card sx={{ p: 2, backgroundColor: '#fff3e0', mt: 2, border: '1px solid #ffb74d' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccessTime sx={{ mr: 1, color: '#f57c00' }} />
                    <Typography variant="subtitle2" fontWeight="bold" color="#f57c00">
                      Time Delay Configuration
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={action.timeDelay?.enabled || false}
                          onChange={(e) => updateAction(index, 'timeDelay.enabled', e.target.checked)}
                          color="warning"
                        />
                      }
                      label="Enable Time Delay"
                      sx={{ ml: 'auto' }}
                    />
                  </Box>

                  {action.timeDelay?.enabled && (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          This action will be executed after the specified delay from when the automation is triggered.
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Days"
                          type="number"
                          value={action.timeDelay?.days || 0}
                          onChange={(e) => updateAction(index, 'timeDelay.days', parseInt(e.target.value) || 0)}
                          inputProps={{ min: 0, max: 365 }}
                          sx={{
                            '& .MuiInputBase-root': { height: '40px' }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Hours"
                          type="number"
                          value={action.timeDelay?.hours || 0}
                          onChange={(e) => updateAction(index, 'timeDelay.hours', parseInt(e.target.value) || 0)}
                          inputProps={{ min: 0, max: 23 }}
                          sx={{
                            '& .MuiInputBase-root': { height: '40px' }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Minutes"
                          type="number"
                          value={action.timeDelay?.minutes || 0}
                          onChange={(e) => updateAction(index, 'timeDelay.minutes', parseInt(e.target.value) || 0)}
                          inputProps={{ min: 0, max: 59 }}
                          sx={{
                            '& .MuiInputBase-root': { height: '40px' }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            <strong>Total Delay:</strong> {
                              (action.timeDelay?.days || 0) > 0 ? `${action.timeDelay.days} day(s), ` : ''
                            }
                            {
                              (action.timeDelay?.hours || 0) > 0 ? `${action.timeDelay.hours} hour(s), ` : ''
                            }
                            {
                              (action.timeDelay?.minutes || 0) > 0 ? `${action.timeDelay.minutes} minute(s)` : ''
                            }
                            {
                              (action.timeDelay?.days || 0) === 0 &&
                                (action.timeDelay?.hours || 0) === 0 &&
                                (action.timeDelay?.minutes || 0) === 0 ? 'No delay' : ''
                            }
                          </Typography>
                        </Alert>
                        {errors[`action_${index}_timeDelay`] && (
                          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                            {errors[`action_${index}_timeDelay`]}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  )}
                </Card>
              )}

              {!action.type && (
                <Box sx={{
                  p: 3,
                  textAlign: 'center',
                  color: 'text.secondary',
                  border: '1px dashed #e0e0e0',
                  borderRadius: 1,
                  backgroundColor: '#fafafa'
                }}>
                  <Typography variant="body2">
                    Select an action type to configure this action
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Card>
        );
      })}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <MDButton
          variant="gradient"
          color="info"
          startIcon={<Add />}
          onClick={addAction}
          sx={{
            fontWeight: 'bold',
            color: '#ffffff',
            '&:hover': {
              color: '#ffffff'
            }
          }}
        >
          Add Action
        </MDButton>

        {wizardData.actions.length > 0 && (
          <MDButton
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => {
              setWizardData(prev => ({ ...prev, actions: [] }));
            }}
            sx={{
              borderColor: '#f44336',
              color: '#f44336',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.04)',
                borderColor: '#f44336'
              }
            }}
          >
            Clear All Actions
          </MDButton>
        )}
      </Box>

      {wizardData.actions.length > 0 && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Execution Order:</strong> Actions will be executed in the order shown above (1, 2, 3...).
          </Typography>
        </Alert>
      )}
    </Box>
    );
  };

  const renderReviewStep = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Review your automation configuration before saving.
      </Alert>

      {/* Auto-generation Summary */}
      {(() => {
        const createActions = wizardData.actions.filter(a => a.type === 'Create Record');
        const autoGeneratedFields = [];
        
        createActions.forEach(action => {
          if (action.parameters?.recordType === 'quotation') {
            autoGeneratedFields.push('Quote name will be generated automatically');
          }
          if (action.parameters?.recordType === 'order') {
            autoGeneratedFields.push('Order number will be generated automatically');
          }
          if (action.parameters?.recordType === 'invoice') {
            autoGeneratedFields.push('Invoice number will be generated automatically');
          }
        });

        return autoGeneratedFields.length > 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Fields that will be auto-generated when automation triggers:
            </Typography>
            {autoGeneratedFields.map((field, index) => (
              <Typography key={index} variant="body2" component="div">
                ✓ {field}
              </Typography>
            ))}
          </Alert>
        ) : null;
      })()}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2 }}>
            <MDTypography variant="h6" fontWeight="bold" gutterBottom>
              Basic Information
            </MDTypography>
            <List dense>
              <ListItem>
                <ListItemText primary="Name" secondary={wizardData.name} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Object" secondary={wizardData.object} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Trigger" secondary={wizardData.trigger} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Status" secondary={wizardData.isActive ? 'Active' : 'Inactive'} />
              </ListItem>
            </List>
          </Card>
        </Grid>

        {/* Custom Fields Information */}
        {(() => {
          const customFieldsFromObject = getAvailableFields().filter(field => field.is_custom);
          return customFieldsFromObject.length > 0 && (
            <Grid item xs={12}>
              <Card sx={{ p: 2 }}>
                <MDTypography variant="h6" fontWeight="bold" gutterBottom>
                  Custom Fields Available ({customFieldsFromObject.length})
                </MDTypography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  The following custom fields are available for {wizardData.object} and can be used in conditions and actions:
                </Typography>
                <Grid container spacing={1}>
                  {customFieldsFromObject.map(field => (
                    <Grid item xs={12} md={6} key={field.id}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        backgroundColor: '#f8f9fa',
                        borderRadius: 1,
                        border: '1px solid #e0e0e0'
                      }}>
                        <Chip
                          label="Custom"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: '16px', fontSize: '0.6rem' }}
                        />
                        <Typography variant="body2" fontWeight="medium">
                          {field.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({field.type})
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Grid>
          );
        })()}

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2 }}>
            <MDTypography variant="h6" fontWeight="bold" gutterBottom>
              Condition Groups ({conditionGroups.length})
            </MDTypography>
            {conditionGroups.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No conditions - will run on every trigger event
              </Typography>
            ) : (
              <Box>
                <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
                  <strong>Logic:</strong> {generateLogicPreview()}
                </Typography>
                <List dense>
                  {conditionGroups.map((group, groupIndex) => (
                    <ListItem key={group.id}>
                      <ListItemText
                        primary={`${group.name} (${group.logic})`}
                        secondary={
                          <Box>
                            {group.conditions.map((condition, condIndex) => {
                              const field = getAvailableFields().find(f => f.id === condition.field);
                              let value = condition.value;
                              if (field?.type === 'picklist') {
                                value = value || '';
                              } else if (field?.type === 'reference' && referenceAutocomplete[field.id]?.options) {
                                const ref = referenceAutocomplete[field.id]?.options.find(opt => opt.id === value);
                                value = ref ? ref.label : value;
                              }
                              return (
                                <Typography key={condition.id} variant="caption" display="block">
                                  {condIndex + 1}. {field?.label || prettify(condition.field)} {condition.operator} {value}
                                </Typography>
                              );
                            })}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <MDTypography variant="h6" fontWeight="bold" gutterBottom>
              Actions ({wizardData.actions.length})
            </MDTypography>
            {wizardData.actions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No actions defined
              </Typography>
            ) : (
              <List dense>
                {wizardData.actions.map((action, index) => {
                  // Helper to get field label/value for Create/Update actions
                  const getFieldLabel = (fields, id) => fields?.find(f => f.id === id)?.label || prettify(id);
                  const getFieldValue = (field, value) => {
                    if (field?.type === 'picklist') return value;
                    if (field && typeof value === 'object' && value.mode === 'relative') {
                      return `After ${value.amount} ${value.unit}`;
                    }
                    if (field?.type === 'reference' && referenceAutocomplete[field.id]?.options) {
                      const ref = referenceAutocomplete[field.id]?.options.find(opt => opt.id === value);
                      return ref ? ref.label : value;
                    }
                    return value;
                  };
                  // Render details for each action type
                  let details = null;
                  if (action.type === 'Create Task') {
                    details = (
                      <>
                        <div>Subject: {action.parameters?.subject || ' '}</div>
                        <div>Description: {action.parameters?.notes || ' '}</div>
                        <div>Assigned To: {action.parameters?.assignedTo || ' '}</div>
                        <div>Priority: {action.parameters?.priority || ' '}</div>
                        <div>Related To: {action.parameters?.related_type || ' '}</div>
                        <div>Related Entity: {action.parameters?.related_id || ' '}</div>
                      </>
                    );
                  } else if (action.type === 'Send Email') {
                    details = (
                      <>
                        <div>Template: {action.parameters?.template || ' '}</div>
                        <div>To: {action.parameters?.toEmail || ' '}</div>
                        <div>CC: {action.parameters?.ccEmail || ' '}</div>
                        <div>Subject Override: {action.parameters?.subjectOverride || ' '}</div>
                        <div>Email Content: {action.parameters?.content ? (action.parameters.content.length > 100 ? action.parameters.content.substring(0, 100) + '...' : action.parameters.content) : ' '}</div>
                      </>
                    );
                  } else if (action.type === 'Update Field') {
                    const fields = getAvailableFields();
                    const field = fields.find(f => f.id === action.parameters?.field);
                    details = (
                      <>
                        <div>Field: {field?.label || prettify(action.parameters?.field)}</div>
                        <div>New Value: {getFieldValue(field, action.parameters?.value)}</div>
                      </>
                    );
                  } else if (action.type === 'Create Record') {
                    details = (
                      <>
                        <div>Record Type: {action.parameters?.recordType || ' '}</div>
                        {createRecordFields.map(field => (
                          <div key={field.id}>
                            {field.label || prettify(field.id)}: {getFieldValue(field, action.parameters?.[field.id]) || ' '}
                            {/* Show auto-generation indicator */}
                            {action.parameters?.recordType === 'quotation' && field.id === 'quote_name' && (
                              <Chip
                                label="Will generate automatically"
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ ml: 1, height: '16px', fontSize: '0.6rem' }}
                              />
                            )}
                            {action.parameters?.recordType === 'order' && field.id === 'order_number' && (
                              <Chip
                                label="Will generate automatically"
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ ml: 1, height: '16px', fontSize: '0.6rem' }}
                              />
                            )}
                            {action.parameters?.recordType === 'invoice' && field.id === 'invoice_number' && (
                              <Chip
                                label="Will generate automatically"
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ ml: 1, height: '16px', fontSize: '0.6rem' }}
                              />
                            )}

                          </div>
                        ))}
                      </>
                    );
                  } else if (action.type === 'Update Related Record') {
                    const field = relatedRecordFields.find(f => f.id === action.parameters?.relatedField);
                    details = (
                      <>
                        <div>Related Record Type: {action.parameters?.relatedRecordType || ' '}</div>
                        <div>Field to Update: {field?.label || prettify(action.parameters?.relatedField)}</div>
                        <div>New Value: {getFieldValue(field, action.parameters?.relatedValue)}</div>
                      </>
                    );
                  } else if (action.type === 'Webhook') {
                    const fieldMappingCount = action.parameters?.fieldMapping ? Object.keys(action.parameters.fieldMapping).length : 0;
                    const customParamsCount = action.parameters?.customParams?.length || 0;
                    details = (
                      <>
                        <div>Webhook: {action.parameters?.name || ' '}</div>
                        <div>URL: {action.parameters?.url || ' '}</div>
                        <div>Method: {action.parameters?.method || 'GET'}</div>
                        <div>Data Fields: {fieldMappingCount} field(s) selected</div>
                        <div>Custom Parameters: {customParamsCount} parameter(s)</div>
                        {action.parameters?.fieldMapping && Object.keys(action.parameters.fieldMapping).length > 0 && (
                          <div>Fields: {Object.keys(action.parameters.fieldMapping).map(fieldId => {
                            const field = getAvailableFields().find(f => f.id === fieldId);
                            return field?.label || prettify(fieldId);
                          }).join(', ')}</div>
                        )}
                        {action.parameters?.customParams && action.parameters.customParams.length > 0 && (
                          <div>Custom: {action.parameters.customParams.map(p => `${p.key}=${p.value}`).join(', ')}</div>
                        )}
                      </>
                    );
                  } else {
                    details = 'No parameters';
                  }
                  // Add time delay information
                  const timeDelayInfo = action.timeDelay?.enabled ? (
                    <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fff3e0', borderRadius: '4px', border: '1px solid #ffb74d' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                        <AccessTime style={{ fontSize: '16px', marginRight: '4px', color: '#f57c00' }} />
                        <span style={{ fontWeight: 'bold', color: '#f57c00' }}>Time Delay:</span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#e65100' }}>
                        {[
                          action.timeDelay.days > 0 ? `${action.timeDelay.days} day(s)` : null,
                          action.timeDelay.hours > 0 ? `${action.timeDelay.hours} hour(s)` : null,
                          action.timeDelay.minutes > 0 ? `${action.timeDelay.minutes} minute(s)` : null
                        ].filter(Boolean).join(', ') || 'No delay'}
                      </div>
                    </div>
                  ) : null;

                  return (
                    <ListItem key={index} alignItems="flex-start">
                      <ListItemText
                        primary={`${index + 1}. ${action.type}`}
                        secondary={
                          <div>
                            {details}
                            {timeDelayInfo}
                          </div>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderTriggerStep();
      case 1:
        return renderConditionsStep();
      case 2:
        return renderActionsStep();
      case 3:
        return renderReviewStep();
      default:
        return null;
    }
  };

  // Helper to fetch options for a reference field
  // Function to fetch and cache reference data
  const fetchReferenceData = async (endpoint, labelField) => {
    // Check if data is already cached
    if (referenceDataCache[endpoint]) {
      return referenceDataCache[endpoint];
    }

    // Check if already loading
    if (referenceDataLoading[endpoint]) {
      return null; // Will retry when loading completes
    }

    setReferenceDataLoading(prev => ({ ...prev, [endpoint]: true }));

    try {
      // //console.log(`=== CACHE: Fetching data from ${endpoint} (first time) ===`);
      const res = await axiosInstance.get(endpoint);
      const options = (res.data || [])
        .map(item => {
          const label = endpoint.includes('/contacts') ? getContactLabel(item) : (item[labelField] || item.name || item.full_name || item.full_lead_name || item.order_number || 'Unknown');
          return {
            id: item.id,
            label: label
          };
        })
        // Remove duplicates based on label (name) to ensure uniqueness
        .filter((option, index, self) => 
          index === self.findIndex(o => o.label === option.label)
        );

      // Cache the data
      setReferenceDataCache(prev => ({ ...prev, [endpoint]: options }));
      setReferenceDataLoading(prev => ({ ...prev, [endpoint]: false }));

      // //console.log(`=== CACHE: Cached ${options.length} items for ${endpoint} ===`);
      return options;
    } catch (error) {
      // console.error(`=== CACHE: Error fetching from ${endpoint}:`, error);
      setReferenceDataLoading(prev => ({ ...prev, [endpoint]: false }));
      return [];
    }
  };

  const fetchReferenceOptions = async (fieldId, endpoint, labelField, inputValue = '') => {
    setReferenceAutocomplete(prev => ({
      ...prev,
      [fieldId]: { ...(prev[fieldId] || {}), loading: true, input: inputValue }
    }));
    try {
      // If your backend supports query param for search, use it (e.g., /accounts?search=rajesh)
      const url = inputValue ? `${endpoint}?search=${encodeURIComponent(inputValue)}` : endpoint;
      const res = await axiosInstance.get(url);
      setReferenceAutocomplete(prev => ({
        ...prev,
        [fieldId]: {
          ...(prev[fieldId] || {}),
          options: (res.data || []).map(item => ({ id: item.id, label: item[labelField] || item.name || item.full_name || item.full_lead_name || item.order_number })),
          loading: false,
          input: inputValue
        }
      }));
    } catch (e) {
      setReferenceAutocomplete(prev => ({
        ...prev,
        [fieldId]: { ...(prev[fieldId] || {}), loading: false }
      }));
    }
  };

  // Autocomplete component for user selection
  function UserAutocomplete({ value, onChange, label = "User" }) {
    const [isLoading, setIsLoading] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [error, setError] = useState(null);
    const [hasInitialLoad, setHasInitialLoad] = useState(false);

    // Fetch all users
    const fetchUsers = useCallback(async (query = '') => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await axiosInstance.get('/users/');
        let users = (response.data || []).map(user => ({
          id: user.id,
          label: user.fullname || user.username || user.name || 'Unknown User'
        }));

        // If there's a search query, filter the users
        if (query) {
          users = users.filter(user =>
            user.label.toLowerCase().includes(query.toLowerCase())
          );
        }

        setAllUsers(users);
        setFilteredUsers(users);
        setHasInitialLoad(true);
      } catch (error) {
        setError(`Failed to fetch users: ${error.message}`);
        setAllUsers([]);
        setFilteredUsers([]);
      } finally {
        setIsLoading(false);
      }
    }, [axiosInstance]);

    // Debounced fetch function using useMemo to maintain stability
    const debouncedFetchUsers = useMemo(
      () => debounce((searchText) => {
        fetchUsers(searchText);
      }, 300),
      [fetchUsers]
    );

    // Load all users when component mounts
    useEffect(() => {
      if (!hasInitialLoad) {
        fetchUsers();
      }
    }, [fetchUsers, hasInitialLoad]);

    // Handle value change
    const handleChange = useCallback((_, newValue) => {
      onChange(newValue ? newValue.id : '');
    }, [onChange]);

    return (
      <>
        <Autocomplete
          fullWidth
          size="small"
          options={filteredUsers}
          getOptionLabel={option => option.label || ''}
          value={allUsers.find(user => user.id === value) || null}
          onChange={handleChange}
          onInputChange={(_, newInputValue) => {
            debouncedFetchUsers(newInputValue);
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              onFocus={() => {
                if (filteredUsers.length === 0) {
                  fetchUsers(); // Load all users when focused
                }
              }}
              InputProps={{
                ...params.InputProps,
                style: { height: '40px' },
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
          loading={isLoading}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </>
    );
  }

  // Enhanced Autocomplete component for reference fields with "Create New" option
  function ReferenceAutocomplete({ field, value, onChange, actionIndex = null, onOpenCreateModal, referenceMap, referenceDataCache, referenceDataLoading, fetchReferenceData, prettify, isCreatingRecord }) {
    const { endpoint, labelField } = referenceMap[field.id];
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [error, setError] = useState(null);
    const [inputValue, setInputValue] = useState('');

    // Get cached data and loading state
    const cachedOptions = referenceDataCache[endpoint] || [];
    const isLoading = referenceDataLoading[endpoint] || false;

    // Determine if this field supports creating new records
    const recordType = field.id.replace('_id', '');
    const canCreateNew = ['account', 'contact', 'lead', 'case', 'order', 'quotation'].includes(recordType);

    // Load data on mount if not cached
    useEffect(() => {
      const loadData = async () => {
        try {
          const options = await fetchReferenceData(endpoint, labelField);
          if (options) {
            setFilteredOptions(options);
          }
        } catch (error) {
          setError(`Failed to fetch options: ${error.message}`);
        }
      };

      if (!cachedOptions.length && !isLoading) {
        loadData();
      } else if (cachedOptions.length) {
        setFilteredOptions(cachedOptions);
      }
    }, [endpoint, labelField, cachedOptions.length, isLoading, fetchReferenceData]);

    // Handle value change
    const handleChange = useCallback((_, newValue) => {
      if (newValue && newValue.isCreateNew) {
        // Open the create modal for this record type
        if (onOpenCreateModal) {
          onOpenCreateModal(recordType, actionIndex, field.id);
        }
        return;
      }
      onChange(newValue ? newValue.id : '');
    }, [onChange, recordType, actionIndex, field.id, onOpenCreateModal]);

    // Handle input change for filtering
    const handleInputChange = useCallback((_, newInputValue) => {
      setInputValue(newInputValue);
      if (newInputValue) {
        // Filter existing cached options - no API call
        const filtered = cachedOptions.filter(option =>
          option.label.toLowerCase().includes(newInputValue.toLowerCase())
        );
        setFilteredOptions(filtered);
      } else {
        // Show all cached options if input is empty
        setFilteredOptions(cachedOptions);
      }
    }, [cachedOptions]);

    // Always show "Create New" option
    const optionsWithCreateNew = useMemo(() => {
      if (!canCreateNew) {
        return filteredOptions;
      }
      
      return [
        {
          id: 'create_new',
          label: `Create new ${recordType}`,
          isCreateNew: true
        },
        ...filteredOptions
      ];
    }, [filteredOptions, canCreateNew, recordType]);

    return (
      <>
        <Autocomplete
          fullWidth
          size="small"
          options={optionsWithCreateNew}
          getOptionLabel={option => option.label || ''}
          value={cachedOptions.find(opt => opt.id === value) || null}
          onChange={handleChange}
          onInputChange={handleInputChange}
          inputValue={inputValue}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              {option.isCreateNew ? (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Add sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2" color="primary.main" fontWeight="medium">
                    {option.label}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2">{option.label}</Typography>
              )}
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label={field.label || prettify(field.id)}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading || isCreatingRecord ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
          loading={isLoading || isCreatingRecord}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText={isLoading || isCreatingRecord ? "Loading..." : "No options"}
          filterOptions={(x) => x} // Disable built-in filtering since we handle it manually
          disabled={isCreatingRecord}
        />
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
          )}
      </>
    );
  }

  // Helper to get picklist options for Product fields
  const getPicklistOptions = (fieldId, field) => {
    try {
      if (wizardData.object === 'product') {
        if (fieldId === 'category') return Array.isArray(productMeta.categories) ? productMeta.categories : [];
        if (fieldId === 'brand') return Array.isArray(productMeta.brands) ? productMeta.brands : [];
        if (fieldId === 'manufacturer') return Array.isArray(productMeta.manufacturers) ? productMeta.manufacturers : [];
        if (fieldId === 'location') return Array.isArray(productMeta.locations) ? productMeta.locations : [];
      }
      // fallback for other objects or fields
      return Array.isArray(field?.options) ? field.options : [];
    } catch (error) {
      // console.error('Error in getPicklistOptions:', error);
      return [];
    }
  };

  // Time delay validation API
  const validateTimeDelay = async (timeDelayConfig) => {
    try {
      const response = await axiosInstance.post('/automations/validate-time-delay', timeDelayConfig);
      return response.data;
    } catch (error) {
      // console.error('Error validating time delay:', error);
      return {
        valid: false,
        error: error.response?.data?.detail || 'Failed to validate time delay configuration'
      };
    }
  };

  // Get scheduled actions statistics
  const getScheduledActionsStats = async () => {
    try {
      const response = await axiosInstance.get('/automations/scheduled-actions/statistics');
      return response.data;
    } catch (error) {
      // console.error('Error fetching scheduled actions statistics:', error);
      return null;
    }
  };

  // Get scheduled actions status
  const getScheduledActionsStatus = async () => {
    try {
      const response = await axiosInstance.get('/automations/scheduled-actions/status');
      return response.data;
    } catch (error) {
      // console.error('Error fetching scheduled actions status:', error);
      return null;
    }
  };





  // Reset validation errors when automation changes or wizard opens
  useEffect(() => {
    if (open) {
      setShowValidationErrors(false);
      setErrors({});
    }
  }, [open, automation]);

  // Add useEffect to handle modal state changes
  useEffect(() => {
    // Reset modal states when wizard closes
    if (!open) {
      setModalStates({
        account: { open: false, data: null },
        contact: { open: false, data: null },
        lead: { open: false, data: null },
        case: { open: false, data: null },
        order: { open: false, data: null },
        quotation: { open: false, data: null }
      });
      setEnhancedFieldContext({
        actionIndex: null,
        fieldId: null,
        recordType: null
      });
    }
  }, [open]);

  // Update related objects based on selected object for Update Related Record action.
  // Create Record uses createRecordTypeOptions (does not share this state).
  useEffect(() => {
    if (wizardData.object) {
      const relatedObjectsForObject = getRelatedObjectsForUpdateRelatedRecord(wizardData.object);
      // Keep legacy related targets already saved so travel-on does not blank the select.
      const preserved = (wizardData.actions || [])
        .filter((a) => a.type === 'Update Related Record' && a.parameters?.relatedRecordType)
        .map((a) => a.parameters.relatedRecordType);
      let ids = [...relatedObjectsForObject];
      for (const relatedType of preserved) {
        if (LEGACY_CREATE_RECORD_TYPES.includes(relatedType) && !ids.includes(relatedType)) {
          ids = [...ids, relatedType];
        }
      }
      setRelatedObjects(ids);
    }
  }, [wizardData.object, wizardData.actions, showTravel]);

  // Modal management functions for creating related records
  const openCreateModal = (recordType, actionIndex, fieldId) => {
    setModalStates(prev => ({
      ...prev,
      [recordType]: { open: true, data: null }
    }));
    setEnhancedFieldContext({
      actionIndex,
      fieldId,
      recordType
    });
  };

  const closeCreateModal = (recordType) => {
    setModalStates(prev => ({
      ...prev,
      [recordType]: { open: false, data: null }
    }));
    setEnhancedFieldContext({
      actionIndex: null,
      fieldId: null,
      recordType: null
    });
  };

  const handleRecordCreated = async (recordType, newRecord) => {
    try {
      setIsCreatingRecord(true);
      
      // Update the reference data cache with the new record
      const endpoint = referenceMap[`${recordType}_id`]?.endpoint;
      if (endpoint) {
        const labelField = referenceMap[`${recordType}_id`]?.labelField;
        const newOption = {
          id: newRecord.id,
          label: newRecord[labelField] || newRecord.name || newRecord.first_name || 'Unknown'
        };

        setReferenceDataCache(prev => ({
          ...prev,
          [endpoint]: [...(prev[endpoint] || []), newOption]
        }));

        // Update the action parameter with the new record ID
        if (enhancedFieldContext.actionIndex !== null && enhancedFieldContext.fieldId) {
          updateAction(enhancedFieldContext.actionIndex, 'parameters', {
            ...wizardData.actions[enhancedFieldContext.actionIndex]?.parameters,
            [enhancedFieldContext.fieldId]: newRecord.id
          });
        }
      }

      // Close the modal
      closeCreateModal(recordType);

      // Show success message
      setSnackbar({
        open: true,
        message: `${recordType.charAt(0).toUpperCase() + recordType.slice(1)} created successfully!`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error handling record creation:', error);
      setSnackbar({
        open: true,
        message: 'Error creating record. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsCreatingRecord(false);
    }
  };

  // Fetch data required for modals
  const [modalData, setModalData] = useState({
    managers: [],
    currencies: [],
    countries: [],
    loadingCountries: false
  });

  useEffect(() => {
    if (open) {
      const fetchModalData = async () => {
        try {
          // Fetch managers (users)
          const managersResponse = await axiosInstance.get('/users/');
          
          // Fetch currencies
          const currenciesResponse = await axiosInstance.get('/currencies/');
          
          // Fetch countries
          const countriesResponse = await axiosInstance.get('/countries/');
          
          setModalData({
            managers: managersResponse.data || [],
            currencies: currenciesResponse.data || [],
            countries: countriesResponse.data || [],
            loadingCountries: false
          });
        } catch (error) {
          console.error('Error fetching modal data:', error);
          // Set default values if API calls fail
          setModalData({
            managers: [],
            currencies: [{ code: 'INR', name: 'Indian Rupee' }],
            countries: [{ label: 'India', value: 'India' }],
            loadingCountries: false
          });
        }
      };
      
      fetchModalData();
    }
  }, [open, axiosInstance]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <MDTypography variant="h4" fontWeight="bold">
                {automation ? 'Edit Automation' : 'Create Automation'}
              </MDTypography>
            </Box>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              mb: 4,
              '& .MuiStepConnector-root': {
                top: 20,
                left: 'calc(-50% + 20px)',
                right: 'calc(50% + 20px)',
                zIndex: 0,
                '& .MuiStepConnector-line': {
                  borderColor: '#e0e0e0',
                  borderTopWidth: 2
                }
              },
              '& .MuiStepConnector-active .MuiStepConnector-line': {
                borderColor: '#4caf50'
              },
              '& .MuiStepConnector-completed .MuiStepConnector-line': {
                borderColor: '#4caf50'
              },
              '& .MuiStepLabel-root': {
                padding: '0 8px'
              },
              '& .MuiStepLabel-label': {
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#ffffff',
                marginTop: '8px'
              },
              '& .MuiStepLabel-active .MuiStepLabel-label': {
                color: '#ffffff',
                fontWeight: 600
              },
              '& .MuiStepLabel-completed .MuiStepLabel-label': {
                color: '#ffffff',
                fontWeight: 600
              }
            }}
          >
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={({ completed, active }) => (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: completed ? '#4caf50' : active ? '#ff9800' : '#ffffff',
                        color: completed ? '#ffffff' : active ? '#ffffff' : '#1565C8',
                        border: !completed && !active ? '2px solid #1565C8' : 'none',
                        fontSize: '1.25rem',
                        fontWeight: 500,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      {step.icon}
                    </Box>
                  )}
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ minHeight: '400px' }}>
            {renderStepContent()}
          </Box>

        </DialogContent>

        <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
          <MDButton
            onClick={onClose}
            variant="outlined"
          >
            Cancel
          </MDButton>

          <Box>
            {activeStep > 0 && (
              <MDButton
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Back
              </MDButton>
            )}
            {activeStep < steps.length - 1 ? (
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleNext}
              >
                Next
              </MDButton>
            ) : (
              <MDButton
                variant="gradient"
                color="success"
                onClick={handleSave}
              >
                {automation ? 'Edit Automation' : 'Create Automation'}
              </MDButton>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Email Template Preview Dialog */}
      <Dialog open={openPreviewDialog} onClose={() => setOpenPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Preview: {previewTemplate?.name}
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            Format: {previewTemplate?.format?.toUpperCase()}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {previewTemplate?.format === 'html' ? (
              <div
                dangerouslySetInnerHTML={{ __html: previewTemplate?.content || '' }}
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  padding: '20px',
                  backgroundColor: '#ffffff',
                  minHeight: '300px',
                  color: '#000000',
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '14px',
                  lineHeight: '1.6'
                }}
              />
            ) : (
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                  fontFamily: FONT_MONO,
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  minHeight: '300px',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: '#000000'
                }}
              >
                {previewTemplate?.content || ''}
              </pre>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenPreviewDialog(false)}
            color="primary"
            variant="contained"
            sx={{ color: 'white !important' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Content Preview Dialog */}
      <Dialog open={openContentPreviewDialog} onClose={() => setOpenContentPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Email Preview
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            How your email will look when sent
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Email Preview:
            </Typography>
            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '20px',
                backgroundColor: '#ffffff',
                minHeight: '300px',
                fontFamily: 'Arial, sans-serif',
                lineHeight: '1.6',
                color: '#000000',
                fontSize: '14px',
                overflow: 'auto'
              }}
            >
              {previewContent ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: formatEmailPreview(replaceFieldCodesWithSampleData(previewContent))
                  }}
                />
              ) : (
                'No content to preview'
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenContentPreviewDialog(false)}
            color="primary"
            variant="contained"
            sx={{ color: 'white !important' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for copy notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Modal Components for Creating Related Records */}
      
      {/* Account Creation Modal */}
      <AccountFormModal
        isOpen={modalStates.account.open}
        onClose={() => closeCreateModal('account')}
        onSave={async (accountData) => {
          try {
            const response = await axiosInstance.post('/accounts/', accountData);
            await handleRecordCreated('account', response.data);
            return response.data;
          } catch (error) {
            console.error('Error creating account:', error);
            throw error;
          }
        }}
        managers={modalData.managers}
        currencies={modalData.currencies}
        countries={modalData.countries}
        loadingCountries={modalData.loadingCountries}
        defaultCurrency="INR"
        user={user}
      />

      {/* Contact Creation Modal */}
      <ContactFormModal
        isOpen={modalStates.contact.open}
        onClose={() => closeCreateModal('contact')}
        onSave={async (contactData) => {
          try {
            const response = await axiosInstance.post('/contacts/', contactData);
            await handleRecordCreated('contact', response.data);
            return response.data;
          } catch (error) {
            console.error('Error creating contact:', error);
            throw error;
          }
        }}
        managers={modalData.managers}
        countries={modalData.countries}
        loadingCountries={modalData.loadingCountries}
        user={user}
      />

      {/* Lead Creation Modal */}
      <LeadFormModal
        isOpen={modalStates.lead.open}
        onClose={() => closeCreateModal('lead')}
        onSave={async (leadData) => {
          try {
            const response = await axiosInstance.post('/leads/', leadData);
            await handleRecordCreated('lead', response.data);
            return response.data;
          } catch (error) {
            console.error('Error creating lead:', error);
            throw error;
          }
        }}
        agents={[]}
        currencies={modalData.currencies}
        countries={modalData.countries}
        loadingCountries={modalData.loadingCountries}
        defaultCurrency="INR"
        user={user}
      />

      {/* Case Creation Modal */}
      <AddCaseModal
        isOpen={modalStates.case.open}
        onClose={() => closeCreateModal('case')}
        onSave={async (caseData) => {
          try {
            const response = await axiosInstance.post('/cases/', caseData);
            await handleRecordCreated('case', response.data);
            return response.data;
          } catch (error) {
            console.error('Error creating case:', error);
            throw error;
          }
        }}
        preselectedAccount={null}
      />

      {/* Order Creation Modal */}
      <OrderModal
        isOpen={modalStates.order.open}
        onClose={() => closeCreateModal('order')}
        onSave={async (orderData) => {
          try {
            const response = await axiosInstance.post('/orders/', orderData);
            await handleRecordCreated('order', response.data);
            return response.data;
          } catch (error) {
            console.error('Error creating order:', error);
            throw error;
          }
        }}
        dealId={null}
        dealName=""
        onProductsChange={() => {}}
        user={user}
        preselectedAccount={null}
      />

      {/* Quotation Creation Modal */}
      <QuotationModal
        isOpen={modalStates.quotation.open}
        onClose={() => closeCreateModal('quotation')}
        onSave={async (quotationData) => {
          try {
            const response = await axiosInstance.post('/quotations/', quotationData);
            await handleRecordCreated('quotation', response.data);
            return response.data;
          } catch (error) {
            console.error('Error creating quotation:', error);
            throw error;
          }
        }}
        dealId={null}
        dealName=""
        onProductsChange={() => {}}
        user={user}
        resetForm={() => {}}
      />
    </>
  );
});

export default AutomationWizard; 